<template>
  <NotFoundView
    v-if="showEpisodeNotFound"
    :heading="episodeNotFoundHeading"
    :message="episodeNotFoundMessage"
  />
  <main v-else class="single-episode-prototype">
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="assets.logo"
        :background-src="backgroundUrl"
        :mobile-background-src="mobileBackgroundUrl"
        :background-wrap-style="heroBackgroundStyle"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid align-items-end">
          <div class="col-auto hero__posterWrap">
            <a
              class="hero__posterLink"
              data-testid="episode-poster-link"
              :href="seasonPath"
            >
              <div
                :class="episodePosterFrameClasses"
                data-testid="episode-poster-frame"
              >
                <div
                  v-if="episodePosterLoading"
                  class="image-load-frame__skeleton"
                  data-testid="episode-poster-skeleton"
                  aria-hidden="true"
                ></div>
                <img
                  v-if="episodePosterImageSrc"
                  :class="episodePosterImageClasses"
                  :src="episodePosterImageSrc"
                  :alt="`${showTitle} poster`"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  @load="handleEpisodePosterLoad"
                  @error="handleEpisodePosterError"
                >
              </div>
            </a>
          </div>
          <div class="col hero__meta">
            <div class="upper-container">
              <div v-if="hasLoadedEpisodePayload" class="user-event-container">
                <h1 class="item-title" data-testid="episode-show-title">
                  <a class="episode-show-title-link" :href="showPath">{{ displayShowTitle }}</a>
                  <span class="show-release-year font-darker-gray weight300">{{ showYears }}</span>
                </h1>
                <h2 class="single-episode-title" data-testid="episode-title">
                  <span class="single-episode-title__default" data-testid="episode-title-default">
                    {{ episodeTitle }}
                  </span>
                  <span class="single-episode-title__tablet-meta" data-testid="episode-title-tablet-meta">
                    {{ tabletEpisodeMeta }}
                  </span>
                </h2>
                <p class="single-episode-title-tablet" data-testid="episode-title-tablet">
                  {{ episodeTitleOnly }}
                </p>
                <div class="rating">
                  <div class="rate-box">
                    <div class="rate-text">
                      <p>
                        <span data-testid="episode-rate-label">{{ episodeRateLabel }}</span>
                        <span>/ 10</span>
                        <button
                          v-if="isEpisodeRated"
                          class="rating-reset"
                          data-testid="episode-rating-reset"
                          type="button"
                          aria-label="Remove rating"
                          :disabled="episodeRatingPending"
                          @click="removeEpisodeRating"
                        >
                          <svg
                            class="rating-reset__icon"
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                            <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                            <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                          </svg>
                        </button>
                      </p>
                    </div>
                  </div>
                  <div class="rating-stars" @mouseleave="resetRatingPreview">
                    <template v-if="isAuthenticated">
                      <button
                        v-for="star in 10"
                        :key="`desktop-${star}`"
                        data-testid="episode-rating-star"
                        type="button"
                        @mouseenter="previewRating(star)"
                        @click="setEpisodeRating(star)"
                      >
                        <i :class="ratingStarClass(star)"></i>
                      </button>
                    </template>
                    <template v-else>
                      <a
                        v-for="star in 10"
                        :key="`desktop-${star}`"
                        data-testid="episode-rating-star"
                        :href="loginPath"
                        @mouseenter="previewRating(star)"
                      >
                        <i :class="ratingStarClass(star)"></i>
                      </a>
                    </template>
                  </div>
                </div>
                <div class="buttons">
                  <template v-if="isAuthenticated">
                    <button
                      class="fs-btn left-btn"
                      :class="{ 'btn--watched': isEpisodeWatched }"
                      data-testid="episode-watch-action"
                      type="button"
                      :disabled="episodeWatchModalPending"
                      @click="openEpisodeWatchModal"
                    >
                      <div class="default-btn">
                        <span class="mark-as-watched"><i :class="episodeWatchButtonIconClass"></i> {{ episodeWatchButtonText }}</span>
                        <span class="set-as-watched"><i :class="episodeWatchButtonIconClass"></i>{{ episodeWatchButtonShortText }}</span>
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a class="fs-btn left-btn" data-testid="episode-watch-action" :href="loginPath">
                      <div class="default-btn">
                        <span class="mark-as-watched"><i class="ion-plus"></i> Mark as watched</span>
                        <span class="set-as-watched"><i class="ion-plus"></i> Set watched</span>
                      </div>
                    </a>
                  </template>
                  <template v-if="isAuthenticated">
                    <button
                      class="fs-btn right-btn fav-btn"
                      :class="{ 'btn--favourited': isEpisodeFavourited }"
                      data-testid="episode-favourite-action"
                      type="button"
                      :disabled="episodeFavouritePending"
                      @click="toggleEpisodeFavourite"
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i> {{ favouriteButtonText }}
                      </div>
                    </button>
                    <button
                      class="fs-btn right-btn fav-btn-sm"
                      :class="{ 'btn--favourited': isEpisodeFavourited }"
                      type="button"
                      :disabled="episodeFavouritePending"
                      @click="toggleEpisodeFavourite"
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i>{{ favouriteButtonShortText }}
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a class="fs-btn right-btn fav-btn" data-testid="episode-favourite-action" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </a>
                    <a class="fs-btn right-btn fav-btn-sm" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i>Favourite
                      </div>
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <div v-if="hasLoadedEpisodePayload" class="container g-0 episode-hero-tablet-controls">
      <div class="row g-0 episode-hero-tablet-controls__row">
        <div class="col g-0">
          <div class="rating">
            <div class="rate-box">
              <div class="rate-text">
                <p>
                  <span data-testid="episode-rate-label">{{ episodeRateLabel }}</span>
                  <span>/ 10</span>
                  <button
                    v-if="isEpisodeRated"
                    class="rating-reset"
                    data-testid="episode-rating-reset"
                    type="button"
                    aria-label="Remove rating"
                    :disabled="episodeRatingPending"
                    @click="removeEpisodeRating"
                  >
                    <svg
                      class="rating-reset__icon"
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                      <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                      <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                    </svg>
                  </button>
                </p>
              </div>
            </div>
            <div class="rating-stars" @mouseleave="resetRatingPreview">
              <template v-if="isAuthenticated">
                <button
                  v-for="star in 10"
                  :key="`tablet-${star}`"
                  data-testid="episode-rating-star"
                  type="button"
                  @mouseenter="previewRating(star)"
                  @click="setEpisodeRating(star)"
                >
                  <i :class="ratingStarClass(star)"></i>
                </button>
              </template>
              <template v-else>
                <a
                  v-for="star in 10"
                  :key="`tablet-${star}`"
                  data-testid="episode-rating-star"
                  :href="loginPath"
                  @mouseenter="previewRating(star)"
                >
                  <i :class="ratingStarClass(star)"></i>
                </a>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="row g-0 episode-hero-tablet-controls__row">
        <div class="col g-0">
          <div class="buttons">
            <template v-if="isAuthenticated">
              <button
                class="fs-btn left-btn"
                :class="{ 'btn--watched': isEpisodeWatched }"
                data-testid="episode-watch-action-tablet"
                type="button"
                :disabled="episodeWatchModalPending"
                @click="openEpisodeWatchModal"
              >
                <div class="default-btn">
                  <span class="mark-as-watched"><i :class="episodeWatchButtonIconClass"></i> {{ episodeWatchButtonText }}</span>
                  <span class="set-as-watched"><i :class="episodeWatchButtonIconClass"></i>{{ episodeWatchButtonShortText }}</span>
                </div>
              </button>
            </template>
            <template v-else>
              <a class="fs-btn left-btn" data-testid="episode-watch-action-tablet" :href="loginPath">
                <div class="default-btn">
                  <span class="mark-as-watched"><i class="ion-plus"></i> Mark as watched</span>
                  <span class="set-as-watched"><i class="ion-plus"></i> Set watched</span>
                </div>
              </a>
            </template>
            <template v-if="isAuthenticated">
              <button
                class="fs-btn right-btn fav-btn"
                :class="{ 'btn--favourited': isEpisodeFavourited }"
                data-testid="episode-favourite-action-tablet"
                type="button"
                :disabled="episodeFavouritePending"
                @click="toggleEpisodeFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> {{ favouriteButtonText }}
                </div>
              </button>
              <button
                class="fs-btn right-btn fav-btn-sm"
                :class="{ 'btn--favourited': isEpisodeFavourited }"
                type="button"
                :disabled="episodeFavouritePending"
                @click="toggleEpisodeFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i>{{ favouriteButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="fs-btn right-btn fav-btn" data-testid="episode-favourite-action-tablet" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </a>
              <a class="fs-btn right-btn fav-btn-sm" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i>Favourite
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
      <div v-if="latestEpisodeWatchText" class="row g-0 episode-hero-tablet-controls__row episode-watch-summary-row">
        <div class="col g-0">
          <p class="episode-watch-summary" data-testid="episode-latest-watch-text">
            <span class="episode-watch-summary__label">Last watched:&nbsp;</span>
            <span class="episode-watch-summary__detail">{{ latestEpisodeWatchText }}</span>
          </p>
        </div>
      </div>
    </div>

    <section v-if="hasLoadedEpisodePayload" class="mobile g-0 container">
      <div class="row g-0 item-title-mobile">
        <h1>
          <a class="episode-show-title-link" :href="showPath">{{ displayShowTitle }}</a>
          <span class="show-release-year font-darker-gray weight300">{{ showYears }}</span>
        </h1>
        <h2 class="single-episode-title">{{ episodeTitle }}</h2>
      </div>
      <div class="row g-0 votes-row justify-content-between">
        <div class="col g-0 rating-text">
          <p class="rate">
            <span data-testid="episode-rate-label">{{ episodeRateLabel }}</span>
            <span>/ 10</span>
            <button
              v-if="isEpisodeRated"
              class="rating-reset"
              data-testid="episode-rating-reset"
              type="button"
              aria-label="Remove rating"
              :disabled="episodeRatingPending"
              @click="removeEpisodeRating"
            >
              <svg
                class="rating-reset__icon"
                viewBox="0 0 16 16"
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
              </svg>
            </button>
          </p>
        </div>
      </div>
      <div class="row mobile-rating">
        <div class="col rating-stars justify-content-center" @mouseleave="resetRatingPreview">
          <template v-if="isAuthenticated">
            <button
              v-for="star in 10"
              :key="`mobile-${star}`"
              data-testid="episode-rating-star"
              type="button"
              @mouseenter="previewRating(star)"
              @click="setEpisodeRating(star)"
            >
              <i :class="ratingStarClass(star)"></i>
            </button>
          </template>
          <template v-else>
            <a
              v-for="star in 10"
              :key="`mobile-${star}`"
              data-testid="episode-rating-star"
              :href="loginPath"
              @mouseenter="previewRating(star)"
            >
              <i :class="ratingStarClass(star)"></i>
            </a>
          </template>
        </div>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div class="buttons">
            <template v-if="isAuthenticated">
              <button
                class="fs-btn left-btn"
                :class="{ 'btn--watched': isEpisodeWatched }"
                data-testid="episode-watch-action-mobile"
                type="button"
                :disabled="episodeWatchModalPending"
                @click="openEpisodeWatchModal"
              >
                <div class="default-btn">
                  <span class="mark-as-watched"><i :class="episodeWatchButtonIconClass"></i> {{ episodeWatchButtonText }}</span>
                  <span class="set-as-watched"><i :class="episodeWatchButtonIconClass"></i>{{ episodeWatchButtonShortText }}</span>
                </div>
              </button>
            </template>
            <template v-else>
              <a class="fs-btn left-btn" data-testid="episode-watch-action-mobile" :href="loginPath">
                <div class="default-btn">
                  <span class="mark-as-watched"><i class="ion-plus"></i> Mark as watched</span>
                  <span class="set-as-watched"><i class="ion-plus"></i> Set watched</span>
                </div>
              </a>
            </template>
            <template v-if="isAuthenticated">
              <button
                class="fs-btn right-btn fav-btn"
                :class="{ 'btn--favourited': isEpisodeFavourited }"
                type="button"
                :disabled="episodeFavouritePending"
                @click="toggleEpisodeFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> {{ favouriteButtonText }}
                </div>
              </button>
              <button
                class="fs-btn right-btn fav-btn-sm"
                :class="{ 'btn--favourited': isEpisodeFavourited }"
                type="button"
                :disabled="episodeFavouritePending"
                @click="toggleEpisodeFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i>{{ favouriteButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="fs-btn right-btn fav-btn" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </a>
              <a class="fs-btn right-btn fav-btn-sm" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i>Favourite
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
      <div v-if="latestEpisodeWatchText" class="row g-0 episode-watch-summary-row episode-watch-summary-row--mobile">
        <div class="col g-0">
          <p class="episode-watch-summary" data-testid="episode-latest-watch-text">
            <span class="episode-watch-summary__label">Last watched:&nbsp;</span>
            <span class="episode-watch-summary__detail">{{ latestEpisodeWatchText }}</span>
          </p>
        </div>
      </div>
    </section>
    <section
      v-else
      class="mobile g-0 container mobile-hero-placeholder"
      data-testid="episode-mobile-hero-placeholder"
      aria-hidden="true"
    ></section>

    <a class="top-link" id="top"></a>
    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav">
              <li>
                <RouterLink :class="{ active: activeSection === 'overview' }" :to="basePath">Overview</RouterLink>
              </li>
              <li><RouterLink :class="{ active: activeSection === 'lists' }" :to="`${basePath}/lists`">Lists</RouterLink></li>
              <li>
                <RouterLink :class="{ active: activeSection === 'comments' }" :to="`${basePath}/comments`">
                  Comments
                </RouterLink>
              </li>
              <li v-if="isAuthenticated">
                <RouterLink :class="{ active: activeSection === 'you' }" :to="`${basePath}/you`">You</RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div
      v-if="activeSection === 'overview'"
      class="container g-0"
      data-testid="episode-overview-panel"
    >
      <div v-if="latestEpisodeWatchText" class="row g-0 episode-watch-summary-row episode-watch-summary-row--overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-9">
          <p class="episode-watch-summary" data-testid="episode-latest-watch-text">
            <span class="episode-watch-summary__label">Last watched:&nbsp;</span>
            <span class="episode-watch-summary__detail">{{ latestEpisodeWatchText }}</span>
          </p>
        </div>
      </div>
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-9">
          <div
            v-if="prevEpisode || nextEpisode"
            :class="[
              'row g-0 next-prev-episodes',
              { 'next-prev-episodes--no-image': !hasEpisodeImage },
            ]"
          >
            <div class="col-6 g-0 prev-episode-row">
              <div>
                <a
                  v-if="prevEpisode"
                  class="next-episode"
                  data-testid="episode-prev-link"
                  :href="prevEpisode.href"
                >
                  <span class="ion-android-arrow-back"></span> {{ prevEpisode.label }}
                </a>
              </div>
            </div>
            <div class="col-6 g-0 next-episode-row">
              <div>
                <a
                  v-if="nextEpisode"
                  class="next-episode"
                  data-testid="episode-next-link"
                  :href="nextEpisode.href"
                >
                  {{ nextEpisode.label }} <span class="ion-android-arrow-forward"></span>
                </a>
              </div>
            </div>
          </div>
          <div class="film-data film__main">
            <div class="overview-section">
              <div class="ep-image">
                <img
                  v-if="hasEpisodeImage"
                  data-testid="episode-image"
                  :src="episodeImageUrl"
                  :alt="`${showTitle} ${episodeTitle} image`"
                >
                <p class="ep-aired" data-testid="episode-meta">{{ episodeMetaText }}</p>
              </div>
              <div class="overview">
                <p data-testid="episode-overview">
                  <span data-testid="episode-overview-body">{{ displayedEpisodeOverview }}</span>
                  <button
                    v-if="hasExpandableEpisodeOverview"
                    class="overview-toggle"
                    data-testid="episode-overview-toggle"
                    type="button"
                    @click="toggleEpisodeOverview"
                  >
                    <span class="overview-toggle__icon" aria-hidden="true">{{ episodeOverviewExpanded ? '▲' : '▼' }}</span>
                  </button>
                </p>
              </div>
            </div>

            <div class="film-data-section main-cast">
              <div class="title-section">
                <div>
                  <p>Main cast</p>
                </div>
              </div>
              <div v-if="mainCastRows.length > 0" class="cast-list">
                <div v-for="(row, rowIndex) in mainCastRows" :key="`cast-row-${rowIndex}`" class="cast-row">
                  <div
                    v-for="member in row"
                    :key="`${member.actor}-${member.role}`"
                    class="cast-member"
                    data-testid="episode-cast-member"
                  >
                    <div class="cast-image">
                      <a :href="member.url || '#'"><img :src="member.image" alt=""></a>
                    </div>
                    <div class="cast-name">
                      <a :href="member.url || '#'">{{ member.actor }}</a>
                      <p v-if="member.role">{{ member.role }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <p v-else-if="episodeLoaded" class="no-cast-message" data-testid="episode-no-cast">
                No cast available.
              </p>
            </div>

            <div class="film-data-section lists">
              <div class="title-section">
                <div>
                  <p>Lists with this episode</p>
                </div>
                <div>
                  <a class="more-link" :href="`${basePath}/lists`">
                    More lists <span class="with-this-film">with this episode</span> &gt;
                  </a>
                </div>
              </div>
              <TransitionGroup
                v-if="overviewLists.length > 0"
                name="overview-list"
                tag="div"
                class="film-list-stack"
              >
                <div
                  v-for="(episodeList, episodeListIndex) in overviewLists"
                  :key="episodeList.id"
                  class="film-list"
                  data-testid="overview-episode-list"
                >
                  <button
                    v-if="episodeListIndex === 0 && episodeListSuccessTitle"
                    class="film-list-success-notice"
                    :class="{ 'film-list-success-notice--dismissing': episodeListSuccessDismissing }"
                    data-testid="episode-list-success-notice"
                    type="button"
                    @click="dismissEpisodeListSuccessNotice"
                  >
                    This episode was added to list "{{ episodeListSuccessTitle }}".
                  </button>
                  <div class="list-covers">
                    <div v-for="item in episodeList.items" :key="item.id" class="list-cover">
                      <a :href="episodeList.href || '#'"><img :src="item.posterUrl" :alt="`${item.title} poster`"></a>
                    </div>
                  </div>
                  <p><a class="list-title" :href="episodeList.href || '#'">{{ episodeList.title }}</a></p>
                  <CuratedListLabel v-if="episodeList.is_curated" />
                  <p v-else-if="episodeList.official" class="list-owner official-list">Official list</p>
                  <p v-else class="list-owner">by <a :href="episodeList.ownerHref">{{ episodeList.ownerName }}</a></p>
                </div>
              </TransitionGroup>
              <p
                v-else-if="episodeLoaded"
                class="film-lists-empty"
                data-testid="overview-episode-lists-empty"
              >
                This episode doesn't belong any list, yet.
              </p>
              <div class="add-to-list">
                <div>
                  <template v-if="isAuthenticated">
                    <button
                      class="btn-sm"
                      data-testid="episode-list-open"
                      type="button"
                      @click="openEpisodeListModal"
                    >
                      + Add to list
                    </button>
                  </template>
                  <template v-else>
                    <a class="btn-sm" data-testid="episode-list-open" :href="loginPath">+ Add to list</a>
                  </template>
                </div>
              </div>
            </div>

            <div class="film-data-section discussion">
              <div class="title-section">
                <div>
                  <p>Latest comments</p>
                </div>
                <div><a class="more-link" :href="`${basePath}/comments`">View comments &gt;</a></div>
              </div>
            </div>
            <div class="container g-0 comments">
              <div class="row g-0">
                <div class="col g-0">
                  <template v-if="latestOverviewComments.length > 0">
                    <div
                      v-for="comment in latestOverviewComments"
                      :key="comment.id"
                      class="comment-container"
                      data-testid="comment-card"
                    >
                      <div class="comment-author">
                        <a class="comment-owner" :href="comment.userHref">{{ comment.username }}</a> wrote:
                      </div>
                      <div class="comment-date">{{ overviewCommentTimeText(comment) }}</div>
                      <div class="comment-body">
                        <blockquote>
                          {{ overviewCommentPreviewText(comment) }}
                        </blockquote>
                        <a
                          v-if="overviewCommentIsTruncated(comment)"
                          class="more-link comment-preview-link"
                          :href="`${basePath}/comments`"
                        >
                          View full comment
                        </a>
                      </div>
                    </div>
                  </template>
                  <p
                    v-else-if="latestOverviewCommentsLoaded"
                    class="comment-empty-copy"
                    data-testid="latest-comments-empty"
                  >
                    No comments, yet.
                    <a class="more-link" :href="`${basePath}/comments`">Be the first to comment.</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-none d-md-block col-lg-3 ad-section">
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'lists'" class="container g-0" data-testid="lists-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-9">
          <div
            v-if="prevEpisode || nextEpisode"
            :class="[
              'row g-0 next-prev-episodes',
              'next-prev-episodes--lists',
              { 'next-prev-episodes--no-image': !hasEpisodeImage },
            ]"
          >
            <div class="col-6 g-0 prev-episode-row">
              <div>
                <a
                  v-if="prevEpisode"
                  class="next-episode"
                  data-testid="episode-prev-link"
                  :href="prevEpisode.href"
                >
                  <span class="ion-android-arrow-back"></span> {{ prevEpisode.label }}
                </a>
              </div>
            </div>
            <div class="col-6 g-0 next-episode-row">
              <div>
                <a
                  v-if="nextEpisode"
                  class="next-episode"
                  data-testid="episode-next-link"
                  :href="nextEpisode.href"
                >
                  {{ nextEpisode.label }} <span class="ion-android-arrow-forward"></span>
                </a>
              </div>
            </div>
          </div>
          <div class="container g-0 film-lists-panel">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="episode-lists-heading">{{ episodeListsHeading }}</h2>
              </div>
            </div>

            <div class="row g-0 list-addition-row">
              <div class="col g-0">
                <template v-if="isAuthenticated">
                  <button
                    class="btn-sm list-addition-btn"
                    data-testid="episode-list-open"
                    type="button"
                    @click="openEpisodeListModal"
                  >
                    + Add to list
                  </button>
                </template>
                <template v-else>
                  <a class="btn-sm list-addition-btn" data-testid="episode-list-open" :href="loginPath">+ Add to list</a>
                </template>
              </div>
            </div>

            <p v-if="episodeListsPageError" class="film-lists-empty" data-testid="episode-lists-error">
              {{ episodeListsPageError }}
            </p>
            <p v-else-if="episodeListsPageLoading" class="film-lists-empty" data-testid="episode-lists-loading">
              Loading lists...
            </p>
            <p
              v-else-if="episodeListsPageLoaded && episodeLists.length === 0"
              class="film-lists-empty"
              data-testid="episode-lists-empty"
            >
              This episode doesn't belong any list, yet.
            </p>

            <TransitionGroup
              v-else-if="episodeLists.length > 0"
              name="overview-list"
              tag="div"
              class="film-list-stack film-list-stack--page"
            >
              <div
                v-for="(episodeList, episodeListIndex) in episodeLists"
                :key="episodeList.id"
                class="film-list film-list--page"
                data-testid="episode-list-page-entry"
              >
                <button
                  v-if="episodeListIndex === 0 && episodeListSuccessTitle"
                  class="film-list-success-notice"
                  :class="{ 'film-list-success-notice--dismissing': episodeListSuccessDismissing }"
                  data-testid="episode-list-success-notice"
                  type="button"
                  @click="dismissEpisodeListSuccessNotice"
                >
                  This episode was added to list "{{ episodeListSuccessTitle }}".
                </button>
                <div class="list-posters">
                  <div v-for="item in episodeList.items" :key="item.id" class="list-poster">
                    <a :href="episodeList.href || '#'"><img :src="item.posterUrl" :alt="`${item.title} poster`"></a>
                  </div>
                </div>
                <a class="list-title" :href="episodeList.href || '#'">{{ episodeList.title }}</a>
                <CuratedListLabel v-if="episodeList.is_curated" />
                  <p v-else-if="episodeList.official" class="list-owner official-list">Official list</p>
                <p v-else class="list-owner">by <a :href="episodeList.ownerHref">{{ episodeList.ownerName }}</a></p>
              </div>
              <nav
                v-if="showEpisodeListsPagination"
                key="episode-lists-pagination"
                class="episode-lists-pagination episode-lists-pagination--with-mobile"
                aria-label="Episode lists pagination"
              >
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: !canGoToPreviousEpisodeListsPage || episodeListsPageLoading }">
                    <button
                      class="page-link"
                      data-testid="episode-lists-prev"
                      type="button"
                      @click="goToEpisodeListsPage(episodeListsPageNumber - 1)"
                    >
                      Previous
                    </button>
                  </li>
                  <li
                    v-for="pageItem in episodeListsPages"
                    :key="`episode-lists-${pageItem.key}`"
                    class="page-item"
                    :class="{ active: pageItem.page === episodeListsPageNumber, disabled: pageItem.ellipsis }"
                  >
                    <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                      {{ pageItem.label }}
                    </span>
                    <button
                      v-else
                      class="page-link"
                      :data-testid="`episode-lists-page-${pageItem.page}`"
                      type="button"
                      :aria-label="`Go to page ${pageItem.page}`"
                      @click="goToEpisodeListsPage(pageItem.page)"
                    >
                      {{ pageItem.label }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: !canGoToNextEpisodeListsPage || episodeListsPageLoading }">
                    <button
                      class="page-link"
                      data-testid="episode-lists-next"
                      type="button"
                      @click="goToEpisodeListsPage(episodeListsPageNumber + 1)"
                    >
                      Next
                    </button>
                  </li>
                </ul>
                <CompactMobilePagination
                  :current-page="episodeListsPageNumber"
                  :total-pages="episodeListsNumPages"
                  :loading="episodeListsPageLoading"
                  test-id-prefix="episode-lists"
                  @page-change="goToEpisodeListsPage"
                />
              </nav>
            </TransitionGroup>
          </div>
        </div>

        <div class="d-none d-md-block col-lg-3 ad-section">
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'credits'" class="container g-0" data-testid="episode-cast-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-9">
          <div
            v-if="prevEpisode || nextEpisode"
            :class="[
              'row g-0 next-prev-episodes',
              { 'next-prev-episodes--no-image': !hasEpisodeImage },
            ]"
          >
            <div class="col-6 g-0 prev-episode-row">
              <div>
                <a
                  v-if="prevEpisode"
                  class="next-episode"
                  data-testid="episode-prev-link"
                  :href="prevEpisode.href"
                >
                  <span class="ion-android-arrow-back"></span> {{ prevEpisode.label }}
                </a>
              </div>
            </div>
            <div class="col-6 g-0 next-episode-row">
              <div>
                <a
                  v-if="nextEpisode"
                  class="next-episode"
                  data-testid="episode-next-link"
                  :href="nextEpisode.href"
                >
                  {{ nextEpisode.label }} <span class="ion-android-arrow-forward"></span>
                </a>
              </div>
            </div>
          </div>
          <div class="container g-0">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="episode-cast-heading">Cast</h2>
              </div>
            </div>
            <div v-if="episodeCastAvailable && episodeCastNavButtonsVisible" class="row g-0">
              <div class="col g-0">
                <ul class="credits-buttons d-flex list-unstyled">
                  <li>
                    <a
                      class="cast-btn"
                      data-testid="episode-cast-nav-button"
                      href="#regulars"
                      @click.prevent="scrollEpisodeCastToAnchor"
                    >Regulars</a>
                  </li>
                  <li>
                    <a
                      data-testid="episode-cast-nav-button"
                      href="#guest-stars"
                      @click.prevent="scrollEpisodeCastToAnchor"
                    >Guest stars</a>
                  </li>
                </ul>
              </div>
            </div>
            <div v-if="episodeCastAvailable" class="row g-0 credits-filter-container">
              <div class="col g-0">
                <p data-testid="episode-cast-filter-title">Filter cast by person or character name.</p>
                <div class="credits-filter-field">
                  <input
                    id="episode-cast-filter"
                    v-model="episodeCastFilter"
                    data-testid="episode-cast-filter-input"
                    class="credits-filter"
                    type="search"
                    placeholder="Type person or cast member name"
                  >
                </div>
              </div>
            </div>
            <template v-if="episodeCastAvailable">
              <a v-if="episodeRegularsSectionVisible" id="regulars"></a>
              <div v-if="episodeRegularsSectionVisible" class="row g-0 credits-title-section">
                <div class="col g-0">
                  <p data-testid="episode-regulars-title">{{ episodeRegularsTitle }}</p>
                </div>
              </div>
              <div v-if="episodeRegularsSectionVisible" class="row g-0">
                <div class="col g-0">
                  <div
                    v-for="member in filteredCreditsRegulars"
                    :key="`regular-${member.name}-${member.role}`"
                    class="cast-member-row"
                    data-testid="episode-cast-member"
                  >
                    <div class="cast-member-photo">
                      <img :src="member.image" alt="">
                    </div>
                    <div class="names">
                      <div class="cast-member-name">
                        <a :href="member.url || '#'">{{ member.name }}</a>
                      </div>
                      <div v-if="member.role" class="cast-character-name">
                        <p>{{ member.role }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="back-to-top">
                    <a href="#top" @click.prevent="scrollEpisodeCastToAnchor">
                      Back to top <i class="ion-android-arrow-up"></i>
                    </a>
                  </div>
                </div>
              </div>

              <a v-if="episodeGuestStarsSectionVisible" id="guest-stars"></a>
              <div v-if="episodeGuestStarsSectionVisible" class="row g-0 credits-title-section">
                <div class="col g-0">
                  <p data-testid="episode-guest-stars-title">{{ episodeGuestStarsTitle }}</p>
                </div>
              </div>
              <div v-if="episodeGuestStarsSectionVisible" class="row g-0">
                <div class="col g-0">
                  <div
                    v-for="member in filteredCreditsGuestStars"
                    :key="`guest-${member.name}-${member.role}`"
                    class="cast-member-row"
                    data-testid="episode-cast-member"
                  >
                    <div class="cast-member-photo">
                      <img :src="member.image" alt="">
                    </div>
                    <div class="names">
                      <div class="cast-member-name">
                        <a :href="member.url || '#'">{{ member.name }}</a>
                      </div>
                      <div v-if="member.role" class="cast-character-name">
                        <p>{{ member.role }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="back-to-top">
                    <a href="#top" @click.prevent="scrollEpisodeCastToAnchor">
                      Back to top <i class="ion-android-arrow-up"></i>
                    </a>
                  </div>
                </div>
              </div>
            </template>
            <div v-else-if="episodeLoaded" class="row g-0 credits-empty-row">
              <div class="col g-0">
                <p class="no-cast-message no-credits-message" data-testid="episode-no-cast-message">
                  No cast available
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="d-none d-md-block col-lg-3 ad-section">
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'you'" class="container g-0" data-testid="you-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-8 col-lg-8 you-watch-content" data-testid="episode-you-content">
          <div
            v-if="prevEpisode || nextEpisode"
            :class="[
              'row g-0 next-prev-episodes',
              'next-prev-episodes--you',
              { 'next-prev-episodes--no-image': !hasEpisodeImage },
            ]"
          >
            <div class="col-6 g-0 prev-episode-row">
              <div>
                <a
                  v-if="prevEpisode"
                  class="next-episode"
                  data-testid="episode-prev-link"
                  :href="prevEpisode.href"
                >
                  <span class="ion-android-arrow-back"></span> {{ prevEpisode.label }}
                </a>
              </div>
            </div>
            <div class="col-6 g-0 next-episode-row">
              <div>
                <a
                  v-if="nextEpisode"
                  class="next-episode"
                  data-testid="episode-next-link"
                  :href="nextEpisode.href"
                >
                  {{ nextEpisode.label }} <span class="ion-android-arrow-forward"></span>
                </a>
              </div>
            </div>
          </div>
          <div class="container g-0">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="episode-you-heading">{{ episodeYouHeading }}</h2>
              </div>
            </div>
            <p v-if="episodeYouWatchesError" class="film-you-empty" data-testid="episode-you-error">
              {{ episodeYouWatchesError }}
            </p>
            <p v-else-if="episodeYouWatchesLoading" class="film-you-empty" data-testid="episode-you-loading">
              Loading watches...
            </p>
            <p
              v-else-if="episodeYouWatchesLoaded && episodeYouWatches.length === 0"
              class="film-you-empty"
              data-testid="episode-you-empty"
            >
              No watches yet.
            </p>
            <div v-else class="film-you-watch-list" data-testid="episode-you-watch-list">
              <div
                v-for="(watch, watchIndex) in episodeYouWatches"
                :key="watch.id"
                :class="[
                  'film-you-watch-row',
                  watchIndex < episodeYouWatches.length - 1 || episodeYouWatches.length === 1
                    ? 'film-you-watch-row--bordered'
                    : null,
                ]"
                data-testid="episode-you-watch-row"
              >
                <div class="film-you-watch-date-column">
                  <p class="film-you-watch-date" data-testid="episode-you-watch-date">
                    {{ episodeYouWatchDateText(watch) }}
                  </p>
                </div>
                <div class="film-you-watch-format" data-testid="episode-you-watch-format">
                  {{ episodeYouWatchFormatText(watch) }}
                </div>
                <div class="film-you-watch-actions">
                  <button
                    class="film-you-watch-action"
                    data-testid="episode-you-watch-edit"
                    type="button"
                    aria-label="Edit watch"
                    @click="openEpisodeWatchEditModal(watch)"
                  >
                    <i class="ion-edit" aria-hidden="true"></i>
                  </button>
                  <button
                    class="film-you-watch-action"
                    data-testid="episode-you-watch-remove"
                    type="button"
                    aria-label="Remove watch"
                    @click="removeEpisodeYouWatch(watch)"
                  >
                    <i class="ion-ios-close-outline" aria-hidden="true"></i>
                  </button>
                </div>
                <p
                  v-if="watch.description"
                  class="film-you-watch-description"
                  data-testid="episode-you-watch-description"
                >
                  "{{ watch.description }}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-9">
          <div
            v-if="prevEpisode || nextEpisode"
            :class="[
              'row g-0 next-prev-episodes',
              { 'next-prev-episodes--no-image': !hasEpisodeImage },
            ]"
          >
            <div class="col-6 g-0 prev-episode-row">
              <div>
                <a
                  v-if="prevEpisode"
                  class="next-episode"
                  data-testid="episode-prev-link"
                  :href="prevEpisode.href"
                >
                  <span class="ion-android-arrow-back"></span> {{ prevEpisode.label }}
                </a>
              </div>
            </div>
            <div class="col-6 g-0 next-episode-row">
              <div>
                <a
                  v-if="nextEpisode"
                  class="next-episode"
                  data-testid="episode-next-link"
                  :href="nextEpisode.href"
                >
                  {{ nextEpisode.label }} <span class="ion-android-arrow-forward"></span>
                </a>
              </div>
            </div>
          </div>
          <CommentsPanel
            :base-path="basePath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            target-type="episode"
            :target-id="episode.id"
            test-id-prefix="episode"
          />
        </div>

        <div class="d-none d-md-block col-lg-3 ad-section">
        </div>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="episodeListModalOpen"
        class="film-watch-modal-overlay"
        data-testid="episode-list-modal-overlay"
      >
        <button
          class="film-watch-modal-backdrop"
          type="button"
          aria-label="Close add to list dialog"
          :disabled="episodeListModalPending"
          @click="closeEpisodeListModal"
        ></button>
        <div
          class="film-watch-modal film-list-modal"
          data-testid="episode-list-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="episode-list-modal-title"
          @click.stop
        >
          <div class="film-watch-modal__header">
            <h2 id="episode-list-modal-title" class="film-watch-modal__title film-list-modal__title">
              Add "{{ episodeTitleOnly }}"<br>to list:
            </h2>
            <button
              class="film-watch-modal__close"
              data-testid="episode-list-close"
              type="button"
              aria-label="Close add to list dialog"
              :disabled="episodeListModalPending"
              @click="closeEpisodeListModal"
            >
              <span class="film-watch-modal__close-icon ion-close-round" aria-hidden="true"></span>
            </button>
          </div>

          <p
            v-if="episodeListModalError"
            class="film-watch-modal__error"
            data-testid="episode-list-error"
          >
            {{ episodeListModalError }}
          </p>

          <form
            v-if="!episodeListCreateMode"
            class="film-watch-modal__form"
            @submit.prevent="submitEpisodeListAddition"
          >
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__field">
                <span>List</span>
                <div class="film-list-picker" @click.stop @mousedown.stop>
                  <button
                    class="film-list-picker__toggle"
                    :class="{ 'film-list-picker__toggle--open': episodeListPickerOpen }"
                    data-testid="episode-list-select"
                    type="button"
                    :disabled="episodeListModalPending || episodeListModalLoading"
                    @click.stop="episodeListPickerOpen = !episodeListPickerOpen"
                  >
                    <span class="film-list-picker__label">{{ selectedEpisodeListLabel }}</span>
                    <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                  </button>
                  <div
                    v-if="episodeListPickerOpen"
                    class="film-list-picker__menu"
                    data-testid="episode-list-picker-menu"
                  >
                    <button
                      class="film-list-picker__option"
                      :class="{ 'film-list-picker__option--selected': !episodeListSelectedListId }"
                      data-testid="episode-list-option-empty"
                      type="button"
                      @click.stop="selectEpisodeList('')"
                    >
                      Choose list...
                    </button>
                    <button
                      v-for="episodeList in episodeListOptions"
                      :key="episodeList.id"
                      class="film-list-picker__option"
                      :class="{ 'film-list-picker__option--selected': String(episodeList.id) === episodeListSelectedListId }"
                      :data-testid="`episode-list-option-${episodeList.id}`"
                      type="button"
                      @click.stop="selectEpisodeList(String(episodeList.id))"
                    >
                      {{ episodeList.title }}
                    </button>
                  </div>
                </div>
                <select
                  v-model="episodeListSelectedListId"
                  :disabled="episodeListModalPending || episodeListModalLoading"
                  class="film-list-picker__native"
                  tabindex="-1"
                  aria-hidden="true"
                >
                  <option value="">Choose list...</option>
                  <option
                    v-for="episodeList in episodeListOptions"
                    :key="episodeList.id"
                    :value="String(episodeList.id)"
                  >
                    {{ episodeList.title }}
                  </option>
                </select>
              </div>
            </section>

            <div class="film-watch-modal__actions film-list-modal__actions">
              <button
                class="film-watch-modal__action film-watch-modal__action--secondary"
                data-testid="episode-list-create-toggle"
                type="button"
                :disabled="episodeListModalPending || episodeListModalLoading"
                @click="openEpisodeListCreateMode"
              >
                Create new list
              </button>
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  data-testid="episode-list-cancel"
                  type="button"
                  :disabled="episodeListModalPending"
                  @click="closeEpisodeListModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="episode-list-add-submit"
                  type="submit"
                  :disabled="episodeListModalPending || episodeListModalLoading || !episodeListSelectedListId"
                >
                  Add to list
                </button>
              </div>
            </div>
          </form>

          <form
            v-else
            class="film-watch-modal__form"
            @submit.prevent="submitEpisodeListCreate"
          >
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Create a new list</h3>
              </div>
              <label class="film-watch-modal__field">
                <span>Title</span>
                <input
                  v-model="episodeListCreateTitle"
                  class="film-list-modal__input"
                  data-testid="episode-list-create-title"
                  type="text"
                  maxlength="255"
                  :disabled="episodeListModalPending"
                >
              </label>
              <label class="film-watch-modal__field">
                <span>Description</span>
                <textarea
                  v-model="episodeListCreateDescription"
                  class="film-watch-modal__textarea"
                  data-testid="episode-list-create-description"
                  rows="5"
                  :disabled="episodeListModalPending"
                ></textarea>
              </label>
              <label class="film-watch-modal__checkbox">
                <input
                  v-model="episodeListCreatePublic"
                  data-testid="episode-list-create-public"
                  type="checkbox"
                  :disabled="episodeListModalPending"
                >
                <span>Public</span>
              </label>
            </section>

            <div class="film-watch-modal__actions">
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  data-testid="episode-list-create-cancel"
                  type="button"
                  :disabled="episodeListModalPending"
                  @click="closeEpisodeListModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="episode-list-create-submit"
                  type="submit"
                  :disabled="episodeListModalPending"
                >
                  Create
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div
        v-if="episodeWatchModalOpen"
        class="film-watch-modal-overlay"
        data-testid="episode-watch-modal-overlay"
      >
        <button
          class="film-watch-modal-backdrop"
          type="button"
          aria-label="Close watch modal"
          :disabled="episodeWatchModalPending"
          @click="closeEpisodeWatchModal"
        ></button>
        <div
          class="film-watch-modal episode-watch-modal"
          data-testid="episode-watch-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="episode-watch-modal-title"
        >
          <div class="film-watch-modal__header">
            <div class="episode-watch-modal__title-wrap">
              <h2 id="episode-watch-modal-title" class="film-watch-modal__title">
                {{ episodeWatchModalTitle }}
              </h2>
              <p class="episode-watch-modal__subtitle">{{ episodeWatchModalSubtitle }}</p>
            </div>
            <button
              class="film-watch-modal__close"
              data-testid="episode-watch-close"
              type="button"
              aria-label="Close watch modal"
              :disabled="episodeWatchModalPending"
              @click="closeEpisodeWatchModal"
            >
              <svg
                class="film-watch-modal__close-icon"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.4" />
                <path
                  d="M7 7 L13 13 M13 7 L7 13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <p
            v-if="episodeWatchHasExisting && !episodeWatchEditing"
            class="film-watch-modal__note"
            data-testid="episode-watch-existing-note"
          >
            You already have {{ episodeWatchExistingLabel }} for this episode. Saving will add a new watch.
          </p>

          <p
            v-if="episodeWatchModalError"
            class="film-watch-modal__error"
            data-testid="episode-watch-error"
          >
            {{ episodeWatchModalError }}
          </p>

          <form class="film-watch-modal__form" @submit.prevent="saveEpisodeWatch">
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Watch date</h3>
                <p>Optional</p>
              </div>
              <div class="film-watch-modal__date-grid">
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': episodeWatchDatePickerOpen === 'year' }"
                >
                  <span>Year</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': episodeWatchDatePickerOpen === 'year' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': episodeWatchDatePickerOpen === 'year' }"
                      data-testid="episode-watch-year-picker"
                      type="button"
                      :disabled="episodeWatchModalPending"
                      @click="toggleEpisodeWatchDatePicker('year')"
                    >
                      <span class="film-list-picker__label">{{ selectedEpisodeWatchYearLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="episodeWatchDatePickerOpen === 'year'"
                      class="film-list-picker__menu"
                      data-testid="episode-watch-year-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !episodeWatchYear }"
                        data-testid="episode-watch-year-option-empty"
                        type="button"
                        @click="selectEpisodeWatchYear('')"
                      >
                        Select year
                      </button>
                      <button
                        v-for="year in episodeWatchYearOptions"
                        :key="year"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': year === episodeWatchYear }"
                        :data-testid="`episode-watch-year-option-${year}`"
                        type="button"
                        @click="selectEpisodeWatchYear(year)"
                      >
                        {{ year }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="episodeWatchYear"
                    data-testid="episode-watch-year"
                    :disabled="episodeWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select year</option>
                    <option v-for="year in episodeWatchYearOptions" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </label>
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': episodeWatchDatePickerOpen === 'month' }"
                >
                  <span>Month</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': episodeWatchDatePickerOpen === 'month' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': episodeWatchDatePickerOpen === 'month' }"
                      data-testid="episode-watch-month-picker"
                      type="button"
                      :disabled="episodeWatchModalPending"
                      @click="toggleEpisodeWatchDatePicker('month')"
                    >
                      <span class="film-list-picker__label">{{ selectedEpisodeWatchMonthLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="episodeWatchDatePickerOpen === 'month'"
                      class="film-list-picker__menu"
                      data-testid="episode-watch-month-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !episodeWatchMonth }"
                        data-testid="episode-watch-month-option-empty"
                        type="button"
                        @click="selectEpisodeWatchMonth('')"
                      >
                        Select month
                      </button>
                      <button
                        v-for="month in episodeWatchMonthOptions"
                        :key="month.value"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': month.value === episodeWatchMonth }"
                        :data-testid="`episode-watch-month-option-${month.value}`"
                        type="button"
                        @click="selectEpisodeWatchMonth(month.value)"
                      >
                        {{ month.label }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="episodeWatchMonth"
                    data-testid="episode-watch-month"
                    :disabled="episodeWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select month</option>
                    <option v-for="month in episodeWatchMonthOptions" :key="month.value" :value="month.value">
                      {{ month.label }}
                    </option>
                  </select>
                </label>
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': episodeWatchDatePickerOpen === 'day' }"
                >
                  <span>Day</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': episodeWatchDatePickerOpen === 'day' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': episodeWatchDatePickerOpen === 'day' }"
                      data-testid="episode-watch-day-picker"
                      type="button"
                      :disabled="episodeWatchModalPending"
                      @click="toggleEpisodeWatchDatePicker('day')"
                    >
                      <span class="film-list-picker__label">{{ selectedEpisodeWatchDayLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="episodeWatchDatePickerOpen === 'day'"
                      class="film-list-picker__menu"
                      data-testid="episode-watch-day-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !episodeWatchDay }"
                        data-testid="episode-watch-day-option-empty"
                        type="button"
                        @click="selectEpisodeWatchDay('')"
                      >
                        Select day
                      </button>
                      <button
                        v-for="day in episodeWatchDayOptions"
                        :key="day"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': day === episodeWatchDay }"
                        :data-testid="`episode-watch-day-option-${day}`"
                        type="button"
                        @click="selectEpisodeWatchDay(day)"
                      >
                        {{ day }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="episodeWatchDay"
                    data-testid="episode-watch-day"
                    :disabled="episodeWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select day</option>
                    <option v-for="day in episodeWatchDayOptions" :key="day" :value="day">
                      {{ day }}
                    </option>
                  </select>
                </label>
                <button
                  class="film-watch-modal__today"
                  data-testid="episode-watch-today"
                  type="button"
                  :disabled="episodeWatchModalPending"
                  @click="setEpisodeWatchToday"
                >
                  Today
                </button>
              </div>
            </section>

            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Episode format</h3>
                <p>Optional</p>
              </div>
              <div class="film-watch-modal__format-groups">
                <div
                  v-for="(formatGroup, groupIndex) in episodeWatchFormatGroups"
                  :key="`episode-watch-format-group-${groupIndex}`"
                  class="film-watch-modal__format-group"
                  :class="{ 'film-watch-modal__format-group--two-col': formatGroup.columns === 2 }"
                >
                  <label
                    v-for="formatOption in formatGroup.items"
                    :key="formatOption.value"
                    class="film-watch-modal__choice"
                  >
                    <input
                      v-model="episodeWatchFormat"
                      :data-testid="`episode-watch-format-${formatOption.value}`"
                      type="radio"
                      name="episode-watch-format"
                      :value="formatOption.value"
                      :disabled="episodeWatchModalPending"
                    >
                    <span>{{ formatOption.label }}</span>
                  </label>
                </div>
              </div>
            </section>

            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Notes to self</h3>
                <p>Optional</p>
              </div>
              <textarea
                v-model="episodeWatchDescription"
                class="film-watch-modal__textarea"
                data-testid="episode-watch-description"
                maxlength="300"
                rows="5"
                :disabled="episodeWatchModalPending"
              ></textarea>
              <div class="film-watch-modal__notes-footer">
                <p class="film-watch-modal__counter">
                  {{ episodeWatchRemainingCharacters }} characters left.
                </p>
                <label class="film-watch-modal__checkbox film-watch-modal__notes-public">
                  <input
                    v-model="episodeWatchPublicDescription"
                    data-testid="episode-watch-public"
                    type="checkbox"
                    :disabled="episodeWatchModalPending"
                  >
                  <span>Public</span>
                </label>
              </div>
            </section>

            <div
              class="film-watch-modal__actions"
              :class="{ 'film-watch-modal__actions--end': episodeWatchEditing }"
            >
              <button
                v-if="!episodeWatchEditing"
                class="film-watch-modal__action film-watch-modal__action--secondary"
                data-testid="episode-watch-clear"
                type="button"
                :disabled="episodeWatchModalPending"
                @click="clearEpisodeWatchForm"
              >
                Clear form
              </button>
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  type="button"
                  :disabled="episodeWatchModalPending"
                  @click="closeEpisodeWatchModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="episode-watch-save"
                  type="submit"
                  :disabled="episodeWatchModalPending"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import CuratedListLabel from '../components/CuratedListLabel.vue'
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-episode/episode-detail.css'
import CommentsPanel from '../components/CommentsPanel.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import NotFoundView from './NotFoundView.vue'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { clipOverviewWithSuffix, hasExpandableOverview } from '../utils/overview'
import { compactPaginationItems } from '../utils/pagination'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'

const route = useRoute()
const capturePageView = usePageViewTracking()
const auth = useAuthStore()
auth.initFromStorage()
const showSlug = computed(() => route.params.showSlug || '')
const seasonNumber = computed(() => route.params.seasonNumber || '')
const episodeNumber = computed(() => route.params.episodeNumber || '')
const episodeTitleSlug = computed(() => route.params.episodeTitleSlug || 'episode')
const activeSection = computed(() => {
  const section = route.params.section || 'overview'
  return section === 'discussion' ? 'comments' : section
})
const seasonEpisodeNumber = computed(() => `${seasonNumber.value}x${episodeNumber.value}`)
const basePath = computed(
  () => `/shows/${showSlug.value}/season/${seasonNumber.value}/episode/${episodeNumber.value}/${episodeTitleSlug.value}`,
)
const loginPath = computed(() => buildLoginPath(route.fullPath || basePath.value))
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const authUsername = computed(() => auth.state.username || 'Account')
const showPath = computed(() => `/shows/${episode.value.showSlug || showSlug.value}`)
const posterPlaceholderImage = '/static/films/img/placeholder-film-camera.svg'
const episodePlaceholderImage = '/static/films/img/placeholder-episode-tv.svg'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'
const hoverRating = ref(0)
const episodeLoaded = ref(false)
const episodeNotFound = ref(false)
const anonymousYouSection = computed(() => activeSection.value === 'you' && !isAuthenticated.value)
const showEpisodeNotFound = computed(() => episodeNotFound.value || anonymousYouSection.value)
const episodeNotFoundHeading = computed(() =>
  anonymousYouSection.value ? 'Page was not found' : 'Episode not found',
)
const episodeNotFoundMessage = computed(() =>
  anonymousYouSection.value
    ? 'The page you were looking for could not be found.'
    : 'The episode you were looking for could not be found.',
)
const episodeFavouritePending = ref(false)
const episodeFavourited = ref(false)
const episodeRatingPending = ref(false)
const episodeRating = ref(null)
const episodeDetailIncludesViewerState = ref(false)
const episodeWatched = ref(false)
const episodeWatchModalOpen = ref(false)
const episodeWatchModalPending = ref(false)
const episodeWatchModalError = ref('')
const episodeWatchEditingId = ref(null)
const episodeListModalOpen = ref(false)
const episodeListModalPending = ref(false)
const episodeListModalLoading = ref(false)
const episodeListModalError = ref('')
const episodeListCreateMode = ref(false)
const episodeListOptions = ref([])
const episodeListSuccessTitle = ref('')
const episodeListSuccessDismissing = ref(false)
const episodeListsPageLoading = ref(false)
const episodeListsPageLoaded = ref(false)
const episodeListsPageError = ref('')
const episodeListsPageNumber = ref(1)
const episodeListsPageSize = ref(10)
const episodeListsPageCount = ref(0)
const episodeListsNumPages = ref(1)
const episodeListsPageEntries = ref([])
const episodeListSelectedListId = ref('')
const episodeListPickerOpen = ref(false)
const episodeListCreateTitle = ref('')
const episodeListCreateDescription = ref('')
const episodeListCreatePublic = ref(true)
const episodeWatchExistingCount = ref(0)
const episodeLatestWatch = ref(null)
const episodeYouWatches = ref([])
const episodeYouWatchesLoading = ref(false)
const episodeYouWatchesLoaded = ref(false)
const episodeYouWatchesError = ref('')
const episodeWatchYear = ref('')
const episodeWatchMonth = ref('')
const episodeWatchDay = ref('')
const episodeWatchDatePickerOpen = ref('')
const episodeWatchFormat = ref('')
const episodeWatchDescription = ref('')
const episodeWatchPublicDescription = ref(false)
const episodeCastFilter = ref('')
const episodeOverviewExpanded = ref(false)
const mobileSearchOpen = ref(false)
const latestOverviewCommentsLoaded = ref(false)
const latestOverviewComments = ref([])
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const monthOptionLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const episodeWatchMonthOptions = monthOptionLabels.map((label, index) => ({
  value: String(index + 1).padStart(2, '0'),
  label,
}))
const episodeWatchFormatGroups = [
  {
    columns: 1,
    items: [
      { value: 'streaming', label: 'Streaming' },
    ],
  },
  {
    columns: 2,
    items: [
      { value: 'blu-ray', label: 'Blu-ray' },
      { value: 'dvd', label: 'DVD' },
      { value: '4k-ultra-hd', label: '4K Ultra HD' },
      { value: 'hd-dvd', label: 'HD-DVD' },
      { value: 'vhs', label: 'VHS' },
    ],
  },
  {
    columns: 2,
    items: [
      { value: 'tv', label: 'TV broadcast' },
      { value: 'digital', label: 'Digital copy' },
    ],
  },
]

const assets = {
  logo: '/static/spa/branding/logo.png',
}

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function createEmptyEpisodeState() {
  return {
    id: null,
    showTitle: '',
    showSlug: '',
    showYears: '',
    episodeTitle: '',
    posterUrl: '',
    seasonUrl: '',
    backgroundUrl: '',
    mobileBackgroundUrl: '',
    episodeImageUrl: '',
    overview: '',
    airdateText: '',
    runtimeText: '',
    prevEpisode: null,
    nextEpisode: null,
    mainCast: [],
    creditsRegulars: [],
    creditsGuestStars: [],
    overviewLists: [],
  }
}

function buildAuthHeaders() {
  return auth.state.access
    ? { Authorization: `Bearer ${auth.state.access}` }
    : {}
}

function normalizeRatingValue(value) {
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 10 ? parsed : null
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

function normalizeLatestEpisodeWatch(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const watched = String(payload.watched || '').trim()
  const createdAt = String(payload.created_at || '').trim()
  if (!watched && !createdAt) {
    return null
  }

  return { watched, createdAt }
}

function applyEpisodeDetailViewerState(data) {
  const viewerState = data?.viewer_state
  if (!viewerState || typeof viewerState !== 'object') {
    episodeDetailIncludesViewerState.value = false
    return
  }

  episodeFavourited.value = Boolean(viewerState.favourited)
  episodeRating.value = normalizeRatingValue(viewerState.rating)
  const watchCount = Number.parseInt(viewerState.watch_count, 10) || 0
  episodeWatched.value = Boolean(viewerState.has_watched) || watchCount > 0
  episodeWatchExistingCount.value = watchCount
  episodeLatestWatch.value = normalizeLatestEpisodeWatch(viewerState.latest_watch)
  episodeDetailIncludesViewerState.value = true
}

function normalizeEpisodeYouWatch(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  return {
    id: payload.id,
    watched: String(payload.watched || '').trim(),
    createdAt: String(payload.created_at || '').trim(),
    format: String(payload.format || '').trim(),
    formatLabel: String(payload.format_label || '').trim(),
    description: String(payload.description || '').trim(),
    publicDescription: Boolean(payload.public_description),
  }
}

function formatEpisodeWatchDateValue(value) {
  const watchedValue = String(value || '').trim()
  const watchedMatch = watchedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!watchedMatch) {
    return ''
  }

  const [, year, month, day] = watchedMatch
  if (month === '00') {
    return year
  }

  const monthName = monthNames[Number(month) - 1]
  if (!monthName) {
    return year
  }

  if (day === '00') {
    return `${monthName}, ${year}`
  }

  return `${monthName} ${Number(day)}, ${year}`
}

function formatLatestEpisodeWatchText(latestWatch) {
  if (!latestWatch) return ''

  const watchedText = formatEpisodeWatchDateValue(latestWatch.watched)
  if (watchedText) {
    return watchedText
  }

  const createdAt = String(latestWatch.createdAt || '').trim()
  if (!createdAt) return ''

  const createdAtDate = new Date(createdAt)
  if (Number.isNaN(createdAtDate.getTime())) return ''

  return `Added ${monthNames[createdAtDate.getUTCMonth()]} ${createdAtDate.getUTCDate()}, ${createdAtDate.getUTCFullYear()}`
}

function formatCreatedAtDateText(createdAt) {
  const value = String(createdAt || '').trim()
  if (!value) return ''

  const createdAtDate = new Date(value)
  if (Number.isNaN(createdAtDate.getTime())) return ''

  return `${monthNames[createdAtDate.getUTCMonth()]} ${createdAtDate.getUTCDate()}, ${createdAtDate.getUTCFullYear()}`
}

function episodeYouWatchDateText(watch) {
  const watchedText = formatEpisodeWatchDateValue(watch?.watched)
  if (watchedText) {
    return watchedText
  }

  const createdAtText = formatCreatedAtDateText(watch?.createdAt)
  if (!createdAtText) {
    return 'No date given'
  }

  return `${createdAtText} (added)`
}

function episodeYouWatchFormatText(watch) {
  const label = watch?.formatLabel || watch?.format || ''
  return label === 'Streaming' ? 'Streamed' : label
}

function episodeYouWatchSortValue(watch) {
  return String(watch?.watched || watch?.createdAt || '').trim()
}

function compareEpisodeYouWatches(a, b) {
  const dateCompare = episodeYouWatchSortValue(b).localeCompare(episodeYouWatchSortValue(a))
  if (dateCompare !== 0) {
    return dateCompare
  }

  return Number(b?.id || 0) - Number(a?.id || 0)
}

function replaceEpisodeYouWatch(updatedWatch) {
  const normalized = normalizeEpisodeYouWatch(updatedWatch)
  if (!normalized) {
    return
  }

  episodeYouWatches.value = episodeYouWatches.value.map((watch) =>
    watch.id === normalized.id ? normalized : watch,
  ).sort(compareEpisodeYouWatches)
}

function removeEpisodeYouWatchFromList(watchId) {
  episodeYouWatches.value = episodeYouWatches.value.filter((watch) => watch.id !== watchId)
  episodeYouWatchesLoaded.value = true
}

function formatRelativeTime(value, fallback = '') {
  if (!value) return fallback

  const targetTime = Date.parse(value)
  if (Number.isNaN(targetTime)) return fallback

  const diffMs = targetTime - Date.now()
  const absSeconds = Math.abs(diffMs) / 1000
  const absMinutes = absSeconds / 60
  const absHours = absMinutes / 60
  const absDays = absHours / 24
  const absMonths = absDays / 30
  const rtf = typeof Intl !== 'undefined' && typeof Intl.RelativeTimeFormat === 'function'
    ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
    : null

  if (absSeconds < 60) return rtf ? rtf.format(Math.round(diffMs / 1000), 'second') : fallback
  if (absMinutes < 60) return rtf ? rtf.format(Math.round(diffMs / 60000), 'minute') : fallback
  if (absHours < 24) return rtf ? rtf.format(Math.round(diffMs / 3600000), 'hour') : fallback
  if (absDays < 30) return rtf ? rtf.format(Math.round(diffMs / 86400000), 'day') : fallback
  if (absMonths < 12) return rtf ? rtf.format(Math.round(diffMs / 2592000000), 'month') : fallback
  return rtf ? rtf.format(Math.round(diffMs / 31536000000), 'year') : fallback
}

function normalizeOverviewComment(comment) {
  return {
    id: comment.id,
    parentId: comment.parent_id ?? null,
    level: Number(comment.level ?? 0),
    comment: String(comment.comment || ''),
    containsSpoilers: Boolean(comment.contains_spoilers),
    submitDate: comment.submit_date || null,
    submitDateText: comment.submit_date_text || '',
    username: String(comment.username || ''),
    userHref: buildFilmsomniacPath(comment.username),
  }
}

const OVERVIEW_COMMENT_PREVIEW_LIMIT = 500

function clipOverviewCommentPreview(commentText) {
  const previewText = String(commentText || '')
  if (previewText.length <= OVERVIEW_COMMENT_PREVIEW_LIMIT) {
    return previewText
  }

  const clippedText = previewText.slice(0, OVERVIEW_COMMENT_PREVIEW_LIMIT).trimEnd()
  const lastSpaceIndex = clippedText.lastIndexOf(' ')
  const clippedToWord = lastSpaceIndex > 0 ? clippedText.slice(0, lastSpaceIndex) : clippedText
  return `${clippedToWord} ...`
}

function overviewCommentPreviewText(comment) {
  if (comment.containsSpoilers) {
    return 'This comment contains spoilers.'
  }
  return clipOverviewCommentPreview(comment.comment)
}

function overviewCommentIsTruncated(comment) {
  return !comment.containsSpoilers && comment.comment.length > OVERVIEW_COMMENT_PREVIEW_LIMIT
}

function overviewCommentTimeText(comment) {
  return formatRelativeTime(comment.submitDate, comment.submitDateText)
}

function normalizeOverviewListItem(item) {
  if (!item || typeof item !== 'object') {
    return null
  }

  const posterUrl = String(item.poster_url || '').trim()
  const title = String(item.title || '').trim()
  if (!posterUrl || !title) {
    return null
  }

  return {
    id: item.id ?? `${title}-${posterUrl}`,
    title,
    posterUrl,
  }
}

function normalizeEpisodeList(listEntry, itemLimit = 4) {
  if (!listEntry || typeof listEntry !== 'object') {
    return null
  }

  const title = String(listEntry.title || '').trim()
  if (!title) {
    return null
  }

  const items = Array.isArray(listEntry.items)
    ? listEntry.items.map(normalizeOverviewListItem).filter(Boolean).slice(0, itemLimit)
    : []

  return {
    id: listEntry.id ?? title,
    title,
    official: Boolean(listEntry.official),
    is_curated: Boolean(listEntry.is_curated),
    ownerName: String(listEntry.owner_name || '').trim(),
    ownerHref: buildFilmsomniacPath(listEntry.owner_name),
    href: buildEpisodeListPath(listEntry),
    items,
  }
}

function slugifyText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function buildEpisodeListPath(listEntry) {
  if (listEntry?.is_curated && listEntry.url) return listEntry.url
  const listId = Number.parseInt(String(listEntry?.id || ''), 10)
  if (!Number.isInteger(listId) || listId <= 0) {
    return '#'
  }

  const slug = slugifyText(listEntry?.title || '') || 'list'
  return `/lists/episode/${listId}-${slug}`
}

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}

function normalizeOverviewList(listEntry) {
  return normalizeEpisodeList(listEntry, 4)
}

function normalizeEpisodeListsPageEntry(listEntry) {
  return normalizeEpisodeList(listEntry, 8)
}

function prependOverviewList(listEntry) {
  const normalized = normalizeOverviewList(listEntry)
  if (!normalized) {
    return
  }

  const existing = Array.isArray(episode.value.overviewLists) ? episode.value.overviewLists : []
  const nextOverviewLists = [
    normalized,
    ...existing.filter((entry) => String(entry.id) !== String(normalized.id)),
  ].slice(0, 2)

  episode.value = {
    ...episode.value,
    overviewLists: nextOverviewLists,
  }
}

function prependEpisodeListsPageEntry(listEntry) {
  const normalized = normalizeEpisodeListsPageEntry(listEntry)
  if (!normalized) {
    return
  }

  const existing = Array.isArray(episodeListsPageEntries.value) ? episodeListsPageEntries.value : []
  const alreadyPresent = existing.some((entry) => String(entry.id) === String(normalized.id))
  episodeListsPageEntries.value = [
    normalized,
    ...existing.filter((entry) => String(entry.id) !== String(normalized.id)),
  ].slice(0, episodeListsPageSize.value)
  episodeListsPageCount.value = alreadyPresent
    ? Math.max(episodeListsPageCount.value, episodeListsPageEntries.value.length)
    : Math.max(episodeListsPageCount.value + 1, episodeListsPageEntries.value.length)
  episodeListsNumPages.value = Math.max(1, Math.ceil(episodeListsPageCount.value / episodeListsPageSize.value))
  episodeListsPageLoaded.value = true
  episodeListsPageError.value = ''
}

function buildEpisodeWatchValue() {
  if (!episodeWatchYear.value) {
    return ''
  }
  if (!episodeWatchMonth.value) {
    return `${episodeWatchYear.value}-00-00`
  }
  if (!episodeWatchDay.value) {
    return `${episodeWatchYear.value}-${episodeWatchMonth.value}-00`
  }
  return `${episodeWatchYear.value}-${episodeWatchMonth.value}-${episodeWatchDay.value}`
}

function validateEpisodeWatchDateSelection() {
  if (episodeWatchDay.value && !episodeWatchYear.value && !episodeWatchMonth.value) {
    return 'You have to choose year and month'
  }
  if (episodeWatchDay.value && episodeWatchYear.value && !episodeWatchMonth.value) {
    return 'You have to choose month'
  }
  if (!episodeWatchYear.value && (episodeWatchMonth.value || episodeWatchDay.value)) {
    return 'You have to choose year'
  }
  return ''
}

function clearEpisodeWatchForm() {
  episodeWatchYear.value = ''
  episodeWatchMonth.value = ''
  episodeWatchDay.value = ''
  episodeWatchDatePickerOpen.value = ''
  episodeWatchFormat.value = ''
  episodeWatchDescription.value = ''
  episodeWatchPublicDescription.value = false
  episodeWatchModalError.value = ''
}

function setEpisodeWatchFormFromWatch(watch) {
  clearEpisodeWatchForm()

  const watched = String(watch?.watched || '').trim()
  const dateMatch = watched.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  episodeWatchYear.value = dateMatch ? dateMatch[1] : ''
  episodeWatchMonth.value = dateMatch && dateMatch[2] !== '00' ? dateMatch[2] : ''
  episodeWatchDay.value = dateMatch && dateMatch[3] !== '00' ? dateMatch[3] : ''
  episodeWatchFormat.value = String(watch?.format || '').trim()
  episodeWatchDescription.value = String(watch?.description || '').trim()
  episodeWatchPublicDescription.value = Boolean(watch?.publicDescription)
  episodeWatchModalError.value = ''
}

function resetEpisodeListModalForm() {
  episodeListModalError.value = ''
  episodeListCreateMode.value = false
  episodeListSelectedListId.value = ''
  episodeListPickerOpen.value = false
  episodeListCreateTitle.value = ''
  episodeListCreateDescription.value = ''
  episodeListCreatePublic.value = true
}

let episodeListSuccessDismissTimer = null

function clearEpisodeListSuccessNotice(force = false) {
  if (episodeListSuccessDismissTimer) {
    clearTimeout(episodeListSuccessDismissTimer)
    episodeListSuccessDismissTimer = null
  }
  episodeListSuccessDismissing.value = false
  if (force) {
    episodeListSuccessTitle.value = ''
  }
}

function showEpisodeListSuccessNotice(title) {
  clearEpisodeListSuccessNotice(true)
  episodeListSuccessTitle.value = String(title || '').trim()
}

function dismissEpisodeListSuccessNotice() {
  if (!episodeListSuccessTitle.value || episodeListSuccessDismissing.value) {
    return
  }
  episodeListSuccessDismissing.value = true
  episodeListSuccessDismissTimer = setTimeout(() => {
    episodeListSuccessTitle.value = ''
    episodeListSuccessDismissing.value = false
    episodeListSuccessDismissTimer = null
  }, 240)
}

function closeEpisodeListModal(force = false) {
  if (episodeListModalPending.value && !force) {
    return
  }
  episodeListModalOpen.value = false
  episodeListModalLoading.value = false
  resetEpisodeListModalForm()
}

function openEpisodeListCreateMode() {
  episodeListModalError.value = ''
  episodeListPickerOpen.value = false
  episodeListCreateMode.value = true
}

function selectEpisodeList(listId) {
  episodeListSelectedListId.value = String(listId || '')
  episodeListPickerOpen.value = false
}

async function loadOwnedEpisodeLists(currentShowSlug, currentSeasonEpisodeNumber) {
  episodeListOptions.value = []
  episodeListSelectedListId.value = ''
  episodeListPickerOpen.value = false
  episodeListModalLoading.value = true
  episodeListModalError.value = ''

  try {
    const response = await fetch(`/api/v1/episodes/${currentShowSlug}/${currentSeasonEpisodeNumber}/lists`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeEpisodeListModal(true)
      return
    }
    if (!response.ok) {
      episodeListModalError.value = 'Unable to load your lists right now.'
      return
    }
    const data = await response.json()
    episodeListOptions.value = Array.isArray(data.results) ? data.results : []
  } catch {
    episodeListModalError.value = 'Unable to load your lists right now.'
  } finally {
    episodeListModalLoading.value = false
  }
}

async function openEpisodeListModal() {
  if (
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    episodeListModalPending.value
  ) {
    return
  }

  episodeListModalOpen.value = true
  resetEpisodeListModalForm()
  await loadOwnedEpisodeLists(showSlug.value, seasonEpisodeNumber.value)
}

function closeEpisodeWatchModal(force = false) {
  if (episodeWatchModalPending.value && !force) {
    return
  }
  episodeWatchModalOpen.value = false
  episodeWatchModalError.value = ''
  episodeWatchEditingId.value = null
  episodeWatchDatePickerOpen.value = ''
}

function setEpisodeWatchToday() {
  const now = new Date()
  episodeWatchYear.value = String(now.getUTCFullYear())
  episodeWatchMonth.value = String(now.getUTCMonth() + 1).padStart(2, '0')
  episodeWatchDay.value = String(now.getUTCDate()).padStart(2, '0')
  episodeWatchDatePickerOpen.value = ''
}

function toggleEpisodeWatchDatePicker(pickerName) {
  episodeWatchDatePickerOpen.value = episodeWatchDatePickerOpen.value === pickerName ? '' : pickerName
}

function selectEpisodeWatchYear(year) {
  episodeWatchYear.value = String(year || '')
  episodeWatchDatePickerOpen.value = ''
}

function selectEpisodeWatchMonth(month) {
  episodeWatchMonth.value = String(month || '')
  episodeWatchDatePickerOpen.value = ''
}

function selectEpisodeWatchDay(day) {
  episodeWatchDay.value = String(day || '')
  episodeWatchDatePickerOpen.value = ''
}

const episode = ref(createEmptyEpisodeState())
const episodePosterStatus = ref('loading')

function clipShowTitle(title) {
  const maxLength = 75
  const suffix = ' ...'
  const value = (title || '').trim()
  if (value.length <= maxLength) return value

  const maxBaseLength = maxLength - suffix.length
  let clipped = value.slice(0, maxBaseLength).trimEnd()
  const lastSpaceIndex = clipped.lastIndexOf(' ')
  if (lastSpaceIndex > 0) {
    clipped = clipped.slice(0, lastSpaceIndex)
  }
  return `${clipped}${suffix}`
}

const showTitle = computed(() => episode.value.showTitle || '')
const displayShowTitle = computed(() => clipShowTitle(showTitle.value))
const showYears = computed(() => episode.value.showYears || '')
const episodeTitle = computed(() => episode.value.episodeTitle || '')
const episodeTitleOnly = computed(() => {
  const value = episodeTitle.value.trim()
  return value.replace(/^\d{1,2}x\d{1,2}\s*[•·]\s*/, '') || value
})
const paddedEpisodeCodePart = (value) => {
  const number = Number.parseInt(value, 10)
  return Number.isFinite(number) ? String(number).padStart(2, '0') : String(value || '')
}
const paddedSeasonEpisodeCode = computed(() => (
  `${paddedEpisodeCodePart(seasonNumber.value)}x${paddedEpisodeCodePart(episodeNumber.value)}`
))
const documentTitle = computed(() => {
  if (String(seasonNumber.value) === '0') {
    return buildDocumentTitle([
      showTitle.value,
      'Specials',
      `${episodeNumber.value} ${episodeTitleOnly.value}`,
    ], activeSection.value)
  }

  return buildDocumentTitle([
    `${showTitle.value} ${paddedSeasonEpisodeCode.value}`,
    episodeTitleOnly.value,
  ], activeSection.value)
})
const tabletEpisodeMeta = computed(() => {
  const normalizePart = (value, label) => {
    const numericValue = Number.parseInt(value, 10)
    const displayValue = Number.isNaN(numericValue) ? value : numericValue
    return `${label} ${displayValue}`
  }

  return `${normalizePart(seasonNumber.value, 'Season')} • ${normalizePart(episodeNumber.value, 'Episode')}`
})
const hasLoadedEpisodePayload = computed(() => episodeLoaded.value && !showEpisodeNotFound.value)
const episodePosterLoading = computed(() => episodePosterStatus.value === 'loading')
const episodePosterImageSrc = computed(() => episode.value.posterUrl || '')
const episodePosterFrameClasses = computed(() => [
  'image-load-frame',
  'image-load-frame--poster',
  episodePosterStatus.value === 'loading' ? 'image-load-frame--loading' : null,
  episodePosterStatus.value === 'loaded' ? 'image-load-frame--loaded' : null,
  episodePosterStatus.value === 'fallback' ? 'image-load-frame--fallback' : null,
])
const episodePosterImageClasses = computed(() => [
  'hero__poster',
  'image-load-frame__image',
  episodePosterStatus.value !== 'loading' ? 'image-load-frame__image--visible' : null,
])
const seasonPath = computed(() => (
  episode.value.seasonUrl
  || (showSlug.value && seasonNumber.value
    ? `/shows/${showSlug.value}/season/${seasonNumber.value}`
    : '#')
))
const backgroundUrl = computed(() => episode.value.backgroundUrl || '')
const mobileBackgroundUrl = computed(() => episode.value.mobileBackgroundUrl || '')
const episodeImageUrl = computed(() => episode.value.episodeImageUrl || '')
const hasEpisodeImage = computed(() => {
  const imageUrl = episodeImageUrl.value.trim()
  return Boolean(imageUrl) &&
    !imageUrl.endsWith('placeholder-episode.svg') &&
    !imageUrl.endsWith('placeholder-episode-tv.svg')
})
const overview = computed(() => episode.value.overview || '')
const hasExpandableEpisodeOverview = computed(() => hasExpandableOverview(overview.value))
const displayedEpisodeOverview = computed(() => {
  if (!hasExpandableEpisodeOverview.value || episodeOverviewExpanded.value) {
    return overview.value
  }
  return clipOverviewWithSuffix(overview.value, undefined, '')
})
const episodeMetaText = computed(() => {
  const parts = [episode.value.airdateText || '', episode.value.runtimeText || ''].filter(Boolean)
  return parts.join(' • ')
})
const isEpisodeFavourited = computed(() => episodeFavourited.value)
const favouriteButtonText = computed(() => (isEpisodeFavourited.value ? 'Favourited' : 'Add to favourites'))
const favouriteButtonShortText = computed(() => (isEpisodeFavourited.value ? 'Favourited' : 'Favourite'))
const isEpisodeRated = computed(() => episodeRating.value !== null)
const isEpisodeWatched = computed(() => episodeWatched.value)
const episodeRateLabel = computed(() => (isEpisodeRated.value ? `Rated: ${episodeRating.value}` : 'Rate:'))
const episodeWatchButtonText = computed(() => (isEpisodeWatched.value ? 'Watched' : 'Mark as watched'))
const episodeWatchButtonShortText = computed(() => (isEpisodeWatched.value ? 'Watched' : 'Set as watched'))
const episodeWatchButtonIconClass = computed(() => (isEpisodeWatched.value ? 'ion-checkmark' : 'ion-plus'))
const episodeWatchEditing = computed(() => episodeWatchEditingId.value !== null)
const prevEpisode = computed(() => episode.value.prevEpisode || null)
const nextEpisode = computed(() => episode.value.nextEpisode || null)
const mainCast = computed(() => episode.value.mainCast || [])
const creditsRegulars = computed(() => episode.value.creditsRegulars || [])
const creditsGuestStars = computed(() => episode.value.creditsGuestStars || [])
const episodeCastFilterTerm = computed(() => episodeCastFilter.value.trim().toLowerCase())
const filteredCreditsRegulars = computed(() => filterEpisodeCastMembers(creditsRegulars.value))
const filteredCreditsGuestStars = computed(() => filterEpisodeCastMembers(creditsGuestStars.value))
const episodeCastAvailable = computed(
  () => creditsRegulars.value.length > 0 || creditsGuestStars.value.length > 0,
)
const episodeRegularsSectionVisible = computed(() => filteredCreditsRegulars.value.length > 0)
const episodeGuestStarsSectionVisible = computed(() => filteredCreditsGuestStars.value.length > 0)
const episodeCastNavButtonsVisible = computed(
  () => episodeRegularsSectionVisible.value && episodeGuestStarsSectionVisible.value,
)
const episodeRegularsTitle = computed(() => `Regulars (${filteredCreditsRegulars.value.length})`)
const episodeGuestStarsTitle = computed(() => `Guest stars (${filteredCreditsGuestStars.value.length})`)
const mainCastRows = computed(() => {
  const rows = []
  for (let index = 0; index < mainCast.value.length; index += 2) {
    rows.push(mainCast.value.slice(index, index + 2))
  }
  return rows
})
const heroBackgroundStyle = computed(() => (
  backgroundUrl.value
    ? { backgroundImage: `url(${backgroundUrl.value})` }
    : undefined
))

function resetEpisodePosterLoading(src = episode.value.posterUrl) {
  if (!src) {
    episodePosterStatus.value = 'loading'
    return
  }
  episodePosterStatus.value = src === posterPlaceholderImage ? 'fallback' : 'loading'
}

function handleEpisodePosterLoad() {
  episodePosterStatus.value = episode.value.posterUrl === posterPlaceholderImage
    ? 'fallback'
    : 'loaded'
}

function handleEpisodePosterError() {
  if (episode.value.posterUrl !== posterPlaceholderImage) {
    episode.value = {
      ...episode.value,
      posterUrl: posterPlaceholderImage,
    }
    return
  }
  episodePosterStatus.value = 'fallback'
}

const episodeWatchShowStartYear = computed(() => {
  const match = showYears.value.match(/\d{4}/)
  return match ? match[0] : ''
})
const episodeWatchModalTitle = computed(() => (
  episodeWatchShowStartYear.value
    ? `${showTitle.value} (${episodeWatchShowStartYear.value})`
    : showTitle.value
))
const episodeWatchModalSubtitle = computed(() => {
  const seasonPart = String(Number.parseInt(seasonNumber.value, 10) || 0).padStart(2, '0')
  const episodePart = String(Number.parseInt(episodeNumber.value, 10) || 0).padStart(2, '0')
  const titleValue = episodeTitleOnly.value.trim()
  const clippedTitle = titleValue.length > 50 ? titleValue.slice(0, 50).trimEnd() : titleValue
  return clippedTitle ? `${seasonPart}x${episodePart} ${clippedTitle}` : `${seasonPart}x${episodePart}`
})
const episodeWatchCurrentUtcYear = new Date().getUTCFullYear()
const episodeWatchYearOptions = Array.from(
  { length: episodeWatchCurrentUtcYear - 1899 },
  (_, index) => String(episodeWatchCurrentUtcYear - index),
)
const selectedEpisodeWatchYearLabel = computed(() => episodeWatchYear.value || 'Select year')
const selectedEpisodeWatchMonthLabel = computed(() => (
  episodeWatchMonthOptions.find((month) => month.value === episodeWatchMonth.value)?.label || 'Select month'
))
const selectedEpisodeWatchDayLabel = computed(() => episodeWatchDay.value || 'Select day')
const episodeWatchRemainingCharacters = computed(() => Math.max(0, 300 - episodeWatchDescription.value.length))
const episodeWatchHasExisting = computed(() => episodeWatchExistingCount.value > 0 || episodeWatched.value)
const episodeWatchExistingLabel = computed(() =>
  episodeWatchExistingCount.value > 0 ? pluralize(episodeWatchExistingCount.value, 'watch') : 'watch',
)
const latestEpisodeWatchText = computed(() => formatLatestEpisodeWatchText(episodeLatestWatch.value))
const episodeYouWatchCount = computed(() => episodeYouWatches.value.length)
const episodeYouHeading = computed(() => `Watches (${episodeYouWatchCount.value})`)
const overviewLists = computed(() =>
  Array.isArray(episode.value.overviewLists) ? episode.value.overviewLists : [],
)
const episodeListsHeading = computed(() => `Lists with this episode (${episodeListsPageCount.value})`)
const episodeLists = computed(() =>
  Array.isArray(episodeListsPageEntries.value) ? episodeListsPageEntries.value : [],
)
const showEpisodeListsPagination = computed(() => episodeListsNumPages.value > 1)
const canGoToPreviousEpisodeListsPage = computed(() => episodeListsPageNumber.value > 1)
const canGoToNextEpisodeListsPage = computed(() => episodeListsPageNumber.value < episodeListsNumPages.value)
const episodeListsPages = computed(() => (
  compactPaginationItems(episodeListsPageNumber.value, episodeListsNumPages.value)
))
const selectedEpisodeListLabel = computed(() => {
  const selectedId = String(episodeListSelectedListId.value || '').trim()
  if (!selectedId) {
    return 'Choose list...'
  }
  const selectedList = episodeListOptions.value.find((entry) => String(entry.id) === selectedId)
  return selectedList?.title || 'Choose list...'
})
const episodeWatchSelectedDayCount = computed(() => {
  if (!episodeWatchYear.value || !episodeWatchMonth.value) {
    return 31
  }
  return new Date(
    Date.UTC(
      Number.parseInt(episodeWatchYear.value, 10),
      Number.parseInt(episodeWatchMonth.value, 10),
      0,
    ),
  ).getUTCDate()
})
const episodeWatchDayOptions = computed(() =>
  Array.from({ length: episodeWatchSelectedDayCount.value }, (_, index) =>
    String(index + 1).padStart(2, '0'),
  ),
)

function normalizeMainCast(castEntries) {
  if (!Array.isArray(castEntries)) return []

  return castEntries
    .slice(0, 8)
    .filter((cast) => cast?.actor)
    .map((cast) => ({
      actor: cast.actor,
      role: cast.role || '',
      image: cast.image || '/static/films/img/placeholder-person.svg',
      url: cast.url || '#',
    }))
}

function normalizeCreditsMembers(castEntries) {
  if (!Array.isArray(castEntries)) return []

  return castEntries
    .filter((cast) => cast?.name)
    .map((cast) => ({
      name: cast.name,
      role: cast.role || '',
      image: cast.image || '/static/films/img/placeholder-person.svg',
      url: cast.url || '#',
    }))
}

function filterEpisodeCastMembers(members) {
  if (!episodeCastFilterTerm.value) return members

  return members.filter((member) => {
    const name = String(member.name || '').toLowerCase()
    const role = String(member.role || '').toLowerCase()
    return name.includes(episodeCastFilterTerm.value) || role.includes(episodeCastFilterTerm.value)
  })
}

function scrollEpisodeCastToAnchor(event) {
  const targetId = event?.currentTarget?.getAttribute('href')?.replace('#', '')
  const target = targetId ? document.getElementById(targetId) : null

  if (target && typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

async function loadEpisodeDetail(currentShowSlug, currentSeasonEpisodeNumber) {
  const confirmPageView = capturePageView()
  episode.value = createEmptyEpisodeState()
  episodeCastFilter.value = ''
  episodeOverviewExpanded.value = false
  episodeDetailIncludesViewerState.value = false
  episodeLoaded.value = false
  episodeNotFound.value = false
  if (!currentShowSlug || !currentSeasonEpisodeNumber || typeof fetch !== 'function') {
    episodeLoaded.value = true
    return
  }

  try {
    const authHeaders = buildAuthHeaders()
    const detailUrl = `/api/v1/episodes/${currentShowSlug}/${currentSeasonEpisodeNumber}/?section=${encodeURIComponent(activeSection.value || 'overview')}`
    const response = Object.keys(authHeaders).length > 0
      ? await fetch(
        detailUrl,
        {
          headers: authHeaders,
        },
      )
      : await fetch(detailUrl)
    if (response.status === 404) {
      episodeNotFound.value = true
      return
    }
    if (!response.ok) return
    const data = await response.json()
    if (!showEpisodeNotFound.value) confirmPageView()
    episode.value = {
      id: data.episode_id || null,
      showTitle: data.show_title || '',
      showSlug: data.show_slug || currentShowSlug,
      showYears: data.show_years || '',
      episodeTitle: data.episode_title || '',
      posterUrl: data.poster_url || posterPlaceholderImage,
      seasonUrl: data.season_url || '',
      backgroundUrl: data.background_url || defaultBackgroundImage,
      mobileBackgroundUrl: data.mobile_background_url || '',
      episodeImageUrl: data.episode_image_url || '',
      overview: data.overview || '',
      airdateText: data.airdate_text || '',
      runtimeText: data.runtime_text || '',
      prevEpisode: data.prev_episode || null,
      nextEpisode: data.next_episode || null,
      mainCast: normalizeMainCast(data.main_cast),
      creditsRegulars: normalizeCreditsMembers(data.credits_regulars),
      creditsGuestStars: normalizeCreditsMembers(data.credits_guest_stars),
      overviewLists: Array.isArray(data.overview_lists)
        ? data.overview_lists.map(normalizeOverviewList).filter(Boolean)
        : [],
    }
    applyEpisodeDetailViewerState(data)
  } catch {
    // Keep the neutral loading state visible when the API call fails.
  } finally {
    episodeLoaded.value = true
  }
}

async function loadLatestOverviewComments(episodeId, section = activeSection.value) {
  latestOverviewComments.value = []
  latestOverviewCommentsLoaded.value = false

  if (!episodeId || section !== 'overview' || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/comments/episode/${episodeId}/?top_level_limit=2`)
    if (!response.ok) {
      return
    }
    const data = await response.json()
    const results = Array.isArray(data.results) ? data.results.map(normalizeOverviewComment) : []
    latestOverviewComments.value = results
      .filter((comment) => comment.parentId === null && comment.level === 0)
      .slice(0, 2)
    latestOverviewCommentsLoaded.value = true
  } catch {
    // Keep the section quiet if the preview comments request fails.
  }
}

async function loadEpisodeListsPage(
  currentShowSlug,
  currentSeasonEpisodeNumber,
  section = activeSection.value,
  includeAuth = isAuthenticated.value,
) {
  episodeListsPageEntries.value = []
  episodeListsPageLoading.value = false
  episodeListsPageLoaded.value = false
  episodeListsPageError.value = ''
  episodeListsPageNumber.value = 1
  episodeListsPageSize.value = 10
  episodeListsPageCount.value = 0
  episodeListsNumPages.value = 1

  if (
    !currentShowSlug ||
    !currentSeasonEpisodeNumber ||
    section !== 'lists' ||
    typeof fetch !== 'function'
  ) {
    return
  }

  episodeListsPageLoading.value = true
  const headers = includeAuth ? buildAuthHeaders() : {}

  try {
    const response = Object.keys(headers).length > 0
      ? await fetch(`/api/v1/episodes/${currentShowSlug}/${currentSeasonEpisodeNumber}/lists/page?page=1`, {
        headers,
      })
      : await fetch(`/api/v1/episodes/${currentShowSlug}/${currentSeasonEpisodeNumber}/lists/page?page=1`)
    if ((response.status === 401 || response.status === 403) && Object.keys(headers).length > 0) {
      auth.clearTokens()
      await loadEpisodeListsPage(currentShowSlug, currentSeasonEpisodeNumber, section, false)
      return
    }
    if (!response.ok) {
      episodeListsPageError.value = 'Unable to load lists right now.'
      return
    }
    const data = await response.json()
    episodeListsPageEntries.value = Array.isArray(data.results)
      ? data.results.map(normalizeEpisodeListsPageEntry).filter(Boolean)
      : []
    episodeListsPageNumber.value = Number.parseInt(data.page, 10) || 1
    episodeListsPageSize.value = Number.parseInt(data.page_size, 10) || 10
    episodeListsPageCount.value = Number.parseInt(data.count, 10) || 0
    episodeListsNumPages.value = Math.max(Number.parseInt(data.num_pages, 10) || 1, 1)
    episodeListsPageLoaded.value = true
  } catch {
    episodeListsPageError.value = 'Unable to load lists right now.'
  } finally {
    episodeListsPageLoading.value = false
  }
}

async function goToEpisodeListsPage(pageNumber) {
  if (
    pageNumber < 1 ||
    pageNumber === episodeListsPageNumber.value ||
    pageNumber > episodeListsNumPages.value ||
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  episodeListsPageLoading.value = true
  episodeListsPageError.value = ''
  try {
    const headers = buildAuthHeaders()
    const response = Object.keys(headers).length > 0
      ? await fetch(`/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/lists/page?page=${pageNumber}`, {
        headers,
      })
      : await fetch(`/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/lists/page?page=${pageNumber}`)
    if ((response.status === 401 || response.status === 403) && Object.keys(headers).length > 0) {
      auth.clearTokens()
      await loadEpisodeListsPage(showSlug.value, seasonEpisodeNumber.value, 'lists', false)
      return
    }
    if (!response.ok) {
      episodeListsPageError.value = 'Unable to load lists right now.'
      return
    }
    const data = await response.json()
    episodeListsPageEntries.value = Array.isArray(data.results)
      ? data.results.map(normalizeEpisodeListsPageEntry).filter(Boolean)
      : []
    episodeListsPageNumber.value = Number.parseInt(data.page, 10) || pageNumber
    episodeListsPageSize.value = Number.parseInt(data.page_size, 10) || 10
    episodeListsPageCount.value = Number.parseInt(data.count, 10) || 0
    episodeListsNumPages.value = Math.max(Number.parseInt(data.num_pages, 10) || 1, 1)
    episodeListsPageLoaded.value = true
  } catch {
    episodeListsPageError.value = 'Unable to load lists right now.'
  } finally {
    episodeListsPageLoading.value = false
  }
}

async function fetchEpisodeWatchStatus(currentShowSlug, currentSeasonEpisodeNumber) {
  if (
    !currentShowSlug ||
    !currentSeasonEpisodeNumber ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return null
  }

  const response = await fetch(
    `/api/v1/episodes/${currentShowSlug}/${currentSeasonEpisodeNumber}/watch`,
    {
      headers: buildAuthHeaders(),
    },
  )
  if (response.status === 401 || response.status === 403) {
    auth.clearTokens()
    return null
  }
  if (!response.ok) {
    return null
  }
  return response.json()
}

async function loadEpisodeWatchStatus(currentShowSlug, currentSeasonEpisodeNumber) {
  episodeWatchExistingCount.value = 0

  try {
    const data = await fetchEpisodeWatchStatus(currentShowSlug, currentSeasonEpisodeNumber)
    if (!data) return
    episodeWatchExistingCount.value = Number.parseInt(data.count, 10) || 0
  } catch {
    // Leave the modal usable even if the watch status request fails.
  }
}

async function loadEpisodeWatchedState(currentShowSlug, currentSeasonEpisodeNumber) {
  episodeWatched.value = false
  episodeWatchExistingCount.value = 0
  episodeLatestWatch.value = null

  try {
    const data = await fetchEpisodeWatchStatus(currentShowSlug, currentSeasonEpisodeNumber)
    if (!data) return
    const watchCount = Number.parseInt(data.count, 10) || 0
    episodeWatched.value = Boolean(data.has_watched) || watchCount > 0
    episodeWatchExistingCount.value = watchCount
    episodeLatestWatch.value = normalizeLatestEpisodeWatch(data.latest)
  } catch {
    // Keep the current watched state if the request fails.
  }
}

async function loadEpisodeYouWatches(
  currentShowSlug = showSlug.value,
  currentSeasonEpisodeNumber = seasonEpisodeNumber.value,
  section = activeSection.value,
) {
  if (section !== 'you') {
    return
  }

  episodeYouWatchesError.value = ''

  if (
    !currentShowSlug ||
    !currentSeasonEpisodeNumber ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    episodeYouWatches.value = []
    episodeYouWatchesLoaded.value = true
    episodeYouWatchesLoading.value = false
    return
  }

  episodeYouWatchesLoading.value = true
  try {
    const response = await fetch(
      `/api/v1/episodes/${currentShowSlug}/${currentSeasonEpisodeNumber}/watches`,
      {
        headers: buildAuthHeaders(),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      episodeYouWatches.value = []
      episodeYouWatchesLoaded.value = true
      return
    }
    if (!response.ok) {
      episodeYouWatchesError.value = 'Unable to load watches right now.'
      return
    }
    const data = await response.json()
    const watches = Array.isArray(data.watches)
      ? data.watches.map(normalizeEpisodeYouWatch).filter(Boolean).sort(compareEpisodeYouWatches)
      : []
    const watchCount = Number.parseInt(data.count, 10)
    episodeYouWatches.value = watches
    episodeWatchExistingCount.value = Number.isInteger(watchCount) ? watchCount : watches.length
    episodeWatched.value = episodeWatchExistingCount.value > 0
    episodeLatestWatch.value = null
    episodeYouWatchesLoaded.value = true
  } catch {
    episodeYouWatchesError.value = 'Unable to load watches right now.'
  } finally {
    episodeYouWatchesLoading.value = false
  }
}

async function loadEpisodeFavouriteStatus(currentShowSlug, currentSeasonEpisodeNumber) {
  episodeFavourited.value = false

  if (
    !currentShowSlug ||
    !currentSeasonEpisodeNumber ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  try {
    const response = await fetch(
      `/api/v1/episodes/${currentShowSlug}/${currentSeasonEpisodeNumber}/favourite`,
      {
        headers: buildAuthHeaders(),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    episodeFavourited.value = Boolean(data.favourited)
  } catch {
    // Keep the control in the default state if the favourite status request fails.
  }
}

async function openEpisodeWatchModal() {
  if (
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    episodeWatchModalPending.value
  ) {
    return
  }

  clearEpisodeWatchForm()
  episodeWatchEditingId.value = null
  episodeWatchModalOpen.value = true
  await loadEpisodeWatchStatus(showSlug.value, seasonEpisodeNumber.value)
}

function openEpisodeWatchEditModal(watch) {
  if (!watch || episodeWatchModalPending.value) {
    return
  }

  episodeWatchEditingId.value = watch.id
  setEpisodeWatchFormFromWatch(watch)
  episodeWatchModalOpen.value = true
}

async function removeEpisodeYouWatch(watch) {
  if (
    !watch?.id ||
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  if (!window.confirm('Remove this watch?')) {
    return
  }

  try {
    const response = await fetch(
      `/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/watches/${watch.id}`,
      {
        method: 'DELETE',
        headers: buildAuthHeaders(),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      episodeYouWatchesError.value = 'Unable to remove the watch right now.'
      return
    }

    removeEpisodeYouWatchFromList(watch.id)
    await loadEpisodeWatchedState(showSlug.value, seasonEpisodeNumber.value)
  } catch {
    episodeYouWatchesError.value = 'Unable to remove the watch right now.'
  }
}

async function submitEpisodeListAddition() {
  if (
    episodeListModalPending.value ||
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    !episodeListSelectedListId.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  episodeListModalPending.value = true
  episodeListModalError.value = ''
  try {
    const response = await fetch(`/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/lists`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ list_id: Number.parseInt(episodeListSelectedListId.value, 10) }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeEpisodeListModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      episodeListModalError.value = data.list_id || 'Unable to add this episode to the list right now.'
      return
    }
    showEpisodeListSuccessNotice(data.list?.title)
    prependOverviewList(data.overview_list)
    prependEpisodeListsPageEntry(data.overview_list)
    closeEpisodeListModal(true)
  } catch {
    episodeListModalError.value = 'Unable to add this episode to the list right now.'
  } finally {
    episodeListModalPending.value = false
  }
}

async function submitEpisodeListCreate() {
  if (
    episodeListModalPending.value ||
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  episodeListModalPending.value = true
  episodeListModalError.value = ''
  try {
    const response = await fetch(`/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/lists/create`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: episodeListCreateTitle.value,
        description: episodeListCreateDescription.value,
        public: episodeListCreatePublic.value,
      }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeEpisodeListModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      episodeListModalError.value = data.title || 'Unable to create the list right now.'
      return
    }
    showEpisodeListSuccessNotice(data.list?.title)
    prependOverviewList(data.overview_list)
    prependEpisodeListsPageEntry(data.overview_list)
    closeEpisodeListModal(true)
  } catch {
    episodeListModalError.value = 'Unable to create the list right now.'
  } finally {
    episodeListModalPending.value = false
  }
}

async function loadEpisodeRatingStatus(currentShowSlug, currentSeasonEpisodeNumber) {
  episodeRating.value = null

  if (
    !currentShowSlug ||
    !currentSeasonEpisodeNumber ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  try {
    const response = await fetch(
      `/api/v1/episodes/${currentShowSlug}/${currentSeasonEpisodeNumber}/rating`,
      {
        headers: buildAuthHeaders(),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    episodeRating.value = normalizeRatingValue(data.rating)
  } catch {
    // Keep the control in the default state if the rating status request fails.
  }
}

watch(
  [showSlug, seasonEpisodeNumber, isAuthenticated],
  async ([currentShowSlug, currentSeasonEpisodeNumber, authenticated]) => {
    await loadEpisodeDetail(currentShowSlug, currentSeasonEpisodeNumber)
    if (!authenticated) {
      episodeListModalPending.value = false
      episodeListModalLoading.value = false
      episodeListOptions.value = []
      clearEpisodeListSuccessNotice(true)
      closeEpisodeListModal(true)
      episodeFavourited.value = false
      episodeFavouritePending.value = false
      episodeRating.value = null
      episodeRatingPending.value = false
      episodeWatched.value = false
      episodeWatchExistingCount.value = 0
      episodeLatestWatch.value = null
      episodeYouWatches.value = []
      episodeYouWatchesLoading.value = false
      episodeYouWatchesLoaded.value = activeSection.value === 'you'
      episodeYouWatchesError.value = ''
      episodeWatchModalPending.value = false
      episodeWatchModalOpen.value = false
      episodeWatchEditingId.value = null
      episodeListsPageLoading.value = false
      episodeListsPageLoaded.value = false
      episodeListsPageError.value = ''
      episodeListsPageNumber.value = 1
      episodeListsPageSize.value = 10
      episodeListsPageCount.value = 0
      episodeListsNumPages.value = 1
      episodeListsPageEntries.value = []
      if (activeSection.value === 'lists') {
        await loadEpisodeListsPage(currentShowSlug, currentSeasonEpisodeNumber, 'lists', false)
      }
      return
    }
    if (!episodeDetailIncludesViewerState.value) {
      loadEpisodeWatchedState(currentShowSlug, currentSeasonEpisodeNumber)
      loadEpisodeFavouriteStatus(currentShowSlug, currentSeasonEpisodeNumber)
      loadEpisodeRatingStatus(currentShowSlug, currentSeasonEpisodeNumber)
    }
    if (activeSection.value === 'you') {
      loadEpisodeYouWatches(currentShowSlug, currentSeasonEpisodeNumber, 'you')
    }
    if (activeSection.value === 'lists') {
      loadEpisodeListsPage(currentShowSlug, currentSeasonEpisodeNumber, 'lists')
    }
  },
  { immediate: true },
)

watch(documentTitle, setDocumentTitle, { immediate: true })
watch(() => episode.value.posterUrl, (src) => {
  resetEpisodePosterLoading(src)
}, { immediate: true })

watch([showSlug, seasonEpisodeNumber], () => {
  episodeListOptions.value = []
  clearEpisodeListSuccessNotice(true)
  closeEpisodeListModal(true)
  episodeListsPageLoading.value = false
  episodeListsPageLoaded.value = false
  episodeListsPageError.value = ''
  episodeListsPageNumber.value = 1
  episodeListsPageSize.value = 10
  episodeListsPageCount.value = 0
  episodeListsNumPages.value = 1
  episodeListsPageEntries.value = []
  episodeYouWatches.value = []
  episodeYouWatchesLoading.value = false
  episodeYouWatchesLoaded.value = false
  episodeYouWatchesError.value = ''
})

watch([() => episode.value.id, activeSection], ([episodeId, section]) => {
  loadLatestOverviewComments(episodeId, section)
}, { immediate: true })

watch(activeSection, (section) => {
  if (section === 'lists') {
    loadEpisodeListsPage(showSlug.value, seasonEpisodeNumber.value, section)
  }
  if (section === 'you') {
    loadEpisodeYouWatches(showSlug.value, seasonEpisodeNumber.value, section)
  }
})

watch(episodeWatchYear, (nextYear) => {
  if (!nextYear) {
    episodeWatchMonth.value = ''
    episodeWatchDay.value = ''
    return
  }
  if (
    episodeWatchDay.value &&
    Number.parseInt(episodeWatchDay.value, 10) > episodeWatchSelectedDayCount.value
  ) {
    episodeWatchDay.value = ''
  }
})

watch(episodeWatchMonth, (nextMonth) => {
  if (!nextMonth) {
    episodeWatchDay.value = ''
    return
  }
  if (
    episodeWatchDay.value &&
    Number.parseInt(episodeWatchDay.value, 10) > episodeWatchSelectedDayCount.value
  ) {
    episodeWatchDay.value = ''
  }
})

async function saveEpisodeWatch() {
  if (
    episodeWatchModalPending.value ||
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  episodeWatchModalError.value = ''
  const dateValidationError = validateEpisodeWatchDateSelection()
  if (dateValidationError) {
    episodeWatchModalError.value = dateValidationError
    return
  }

  episodeWatchModalPending.value = true
  try {
    const editingWatchId = episodeWatchEditingId.value
    const response = await fetch(editingWatchId
      ? `/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/watches/${editingWatchId}`
      : `/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/watch`, {
      method: editingWatchId ? 'PATCH' : 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        watched: buildEpisodeWatchValue(),
        format: episodeWatchFormat.value || null,
        description: episodeWatchDescription.value,
        public_description: episodeWatchPublicDescription.value,
        ...(editingWatchId ? {} : { source: 'E' }),
      }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeEpisodeWatchModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      episodeWatchModalError.value =
        data.description ||
        data.watched ||
        data.format ||
        'Unable to save the watch right now.'
      return
    }
    if (editingWatchId) {
      replaceEpisodeYouWatch(data)
      await loadEpisodeWatchedState(showSlug.value, seasonEpisodeNumber.value)
      closeEpisodeWatchModal(true)
      return
    }
    episodeWatchExistingCount.value = Number.parseInt(data.count, 10) || (episodeWatchExistingCount.value + 1)
    episodeWatched.value = Boolean(data.has_watched) || episodeWatchExistingCount.value > 0
    episodeLatestWatch.value = normalizeLatestEpisodeWatch(data.latest || data.watch)
    if (activeSection.value === 'you') {
      await loadEpisodeYouWatches(showSlug.value, seasonEpisodeNumber.value, 'you')
    }
    closeEpisodeWatchModal(true)
  } catch {
    episodeWatchModalError.value = 'Unable to save the watch right now.'
  } finally {
    episodeWatchModalPending.value = false
  }
}

function previewRating(star) {
  hoverRating.value = star
}

function resetRatingPreview() {
  hoverRating.value = 0
}

function ratingStarClass(star) {
  const visibleRating = hoverRating.value || episodeRating.value || 0
  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function toggleEpisodeOverview() {
  if (!hasExpandableEpisodeOverview.value) return
  episodeOverviewExpanded.value = !episodeOverviewExpanded.value
}

async function toggleEpisodeFavourite() {
  if (
    episodeFavouritePending.value ||
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  episodeFavouritePending.value = true
  try {
    const response = await fetch(
      `/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/favourite`,
      {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source: 'E' }),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    episodeFavourited.value = Boolean(data.favourited)
    if (data.watched === true) {
      episodeWatched.value = true
      if (activeSection.value === 'you') {
        await loadEpisodeYouWatches(showSlug.value, seasonEpisodeNumber.value, 'you')
      }
    }
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    episodeFavouritePending.value = false
  }
}

async function setEpisodeRating(ratingValue) {
  if (
    episodeRatingPending.value ||
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  episodeRatingPending.value = true
  try {
    const response = await fetch(
      `/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/rating`,
      {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rating: ratingValue, source: 'E' }),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    episodeRating.value = normalizeRatingValue(data.rating)
    if (data.watched === true) {
      episodeWatched.value = true
      if (activeSection.value === 'you') {
        await loadEpisodeYouWatches(showSlug.value, seasonEpisodeNumber.value, 'you')
      }
    }
    hoverRating.value = 0
  } catch {
    // Leave the current rating unchanged on network or API failure.
  } finally {
    episodeRatingPending.value = false
  }
}

async function removeEpisodeRating() {
  if (
    episodeRatingPending.value ||
    !showSlug.value ||
    !seasonEpisodeNumber.value ||
    !isAuthenticated.value ||
    !isEpisodeRated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  episodeRatingPending.value = true
  try {
    const response = await fetch(
      `/api/v1/episodes/${showSlug.value}/${seasonEpisodeNumber.value}/rating`,
      {
        method: 'DELETE',
        headers: buildAuthHeaders(),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    episodeRating.value = null
    hoverRating.value = 0
  } catch {
    // Leave the current rating unchanged on network or API failure.
  } finally {
    episodeRatingPending.value = false
  }
}

onBeforeUnmount(() => {
  clearEpisodeListSuccessNotice(true)
})

useMetaDescription(() => buildMetaDescription(
  'episode',
  hasLoadedEpisodePayload.value ? { title: episodeTitleOnly.value, showTitle: showTitle.value, code: paddedSeasonEpisodeCode.value } : {},
  activeSection.value,
))
</script>
